import { WORKLOAD, IS_LITE } from '../core/edition';
import { Spatial } from '../core/spatial';
import * as THREE from 'three';
import RAPIER from '@dimforge/rapier3d-compat';
import { seeded, type City, type RoadEdge } from '../world/city';
import { roadLength } from '../world/roads';
import { GROUND } from '../core/config';
import type { PhysicsCity, Disturbance, BlastEvent } from '../physics/world';
import type { FootCache } from '../physics/foot-clearance';
import type { Vec } from '../destruction/structure';
import {
  personGeometry,
  vehicleGeometry,
  animalGeometry,
  trainDoorGeometry,
  type VehicleKind,
} from '../render/geometry';
import { FireSystem, type FireIncident } from './fire';
import type { Effects } from '../render/effects';
import type { AudioSystem } from './audio';
import { RoadNetwork, type RoutePoint } from './routes';
import { WATER_LEVEL, type WaterSystem } from './water';
import type { EntityRegistry } from '../core/entities';
import type { CivilLife } from './civil-life';
import type { Services } from './services';
import type { Railway } from './rail';
import type { Aviation } from './aviation';
import type { Airstrikes } from './airstrikes';
import type { AirSupport } from './air-support';
import type { Buses } from './buses';
import type { Logistics } from './logistics';
import type { ShopStaff } from './shop-staff';
import type { CoastStaff } from './coast-staff';
import type { PowerPlant } from './power-plant';
import type { Military } from './military';
import type { Shelters } from './shelters';
import type { CivilianDrivers } from './civilian-drivers';
import {
  MILITARY_FAMILIES,
  MILITARY_SPECS,
  isMilitaryVehicle,
  militaryTurretGeometry,
} from '../render/military-vehicles';
import type { Marine } from './marine';
import {
  BOAT_FAMILIES,
  RENDER_BOAT_FAMILIES,
  BOAT_SPECS,
  boatGeometry,
  type BoatFamily,
} from '../render/boats';
import type { Naval } from './naval';
import type { Invasion } from './invasion';
import { birdWingGeometry, type BirdFamily } from '../render/wildlife';
import { actorStep } from './scheduling';
import {
  overlapsRoadBody,
  roadBodyPenetration,
  routeBodyOverlaps,
  separatesRoadBody,
  junctionExitPose,
} from './traffic-space';
import { CrossingControl, crossesVehicle } from './crossings';
import { CharacterRenderer, advanceCharacterMotion } from '../render/characters';
import { InteriorOcclusion } from '../render/interior-occlusion';
import {
  ROAD_FAMILIES,
  ROAD_SPECS,
  isRoadFamily,
  roadWheelGeometry,
  busDoorGeometry,
  deliveryDoorGeometry,
} from '../render/road-vehicles';
interface Actor {
  id: string;
  name: string;
  category: 'person' | 'vehicle' | 'animal';
  p: THREE.Vector3;
  previous: THREE.Vector3;
  velocity: THREE.Vector3;
  yaw: number;
  state: string;
  timer: number;
  think: number;
  speed: number;
  color: number;
  variant: number;
  scale: number;
  goal: THREE.Vector3;
  home: THREE.Vector3;
  support?: number;
  layer?: string;
  fallStart?: number;
  hidden?: boolean;
  /** Ambient LOD is separate from boarding, injury and task state. */
  ambientCoarse?: boolean;
  streamedOut?: boolean;
  ambientDebt?: number;
  lodDue?: number;
  carry?: string;
  detour?: THREE.Vector3;
  controlled?: boolean;
  stepDebt?: number;
  stepInterval?: number;
  nextStepAt?: number;
  stepOffset?: number;
  idleSupportRevision?: number;
  resting?: { owner: number; revision: number; x: number; y: number; z: number };
  footCache?: FootCache;
  steeringStop?: {
    x: number;
    y: number;
    z: number;
    goalX: number;
    goalZ: number;
    until: number;
    version: number;
    support: number;
    supportRevision: number;
  };
}
export interface Person extends Actor {
  elite?: boolean;
  combatHealth?: number;
  presentationCoarse?: boolean;
  demobilised?: boolean;
  faction?: 'friendly' | 'hostile';
  fatal?: boolean;
  carrier?: string;
  exposedSeat?: boolean;
  targetPoseStamp?: number;
  renderRoot?: {
    x: number;
    y: number;
    z: number;
    yaw: number;
    scale: number;
    tilt?: number;
    fallen: boolean;
    matrix: THREE.Matrix4;
  };
  poseTilt?: number;
  seatHeight?: number;
  seatBlend?: number;
  yieldTarget?: THREE.Vector3;
  yieldUntil?: number;
  yieldSpeed?: number;
  category: 'person';
  from: number;
  to: number;
  edge: number;
  group: number;
}
export interface Animal extends Actor {
  category: 'animal';
  kind: 'sheep' | 'cow' | 'bird';
  perch?: number;
  birdFamily?: BirdFamily;
  pasture?: number;
  thirst?: number;
  drinks?: number;
  rests?: number;
  restDue?: number;
  flock?: number;
  nextPerch?: number;
  waterVersion?: number;
  waterAvailable?: boolean;
}
export interface TrafficVehicle extends Actor {
  turretYaw?: number;
  turretPitch?: number;
  boatFamily?: BoatFamily;
  cargo?: number;
  carrier?: string;
  junction?: number;
  wheelAngle?: number;
  steerAngle?: number;
  doorOpen?: number;
  parkingYaw?: number;
  category: 'vehicle';
  kind: VehicleKind;
  body: RAPIER.RigidBody;
  collider: RAPIER.Collider;
  previousQ: THREE.Quaternion;
  q: THREE.Quaternion;
  health: number;
  protection?: number;
  heat: number;
  exploded: boolean;
  node: number;
  previousNode: number;
  plan: RoutePoint[];
  targetSpeed: number;
  actualSpeed: number;
  blockedFor: number;
  blockedBy: string;
  progressAt?: number;
  restingVersion?: number;
  restingStamp?: number;
  restingPosition?: THREE.Vector3;
  restingYaw?: number;
  length: number;
  width: number;
  incident?: string;
  station: number;
  riverNext: number;
  riverSign: number;
}
interface Hazard {
  id: string;
  p: THREE.Vector3;
  radius: number;
  life: number;
}
interface Response {
  id: string;
  point: THREE.Vector3;
  status: 'pending' | 'enroute' | 'deployed' | 'unreachable' | 'complete';
  engine?: string;
  police?: string;
  destination?: RoutePoint;
  edge?: number;
  retry: number;
  attempts: number;
  quiet: number;
  water: number;
}
interface Wake {
  p: THREE.Vector3;
  yaw: number;
  life: number;
}
const Y_AXIS = new THREE.Vector3(0, 1, 0),
  PALETTE = [0xb5c9bd, 0xdaaa87, 0xa6b6c9, 0xcdbba0, 0x99b0a6, 0xd2cba8, 0xc7a8a1, 0x859fa9];
const dimensions = (kind: VehicleKind): [number, number, number] =>
  isMilitaryVehicle(kind)
    ? [MILITARY_SPECS[kind].width, MILITARY_SPECS[kind].length, MILITARY_SPECS[kind].height]
    : kind === 'airport-fuel' || kind === 'airport-baggage'
      ? [1.9, 4.6, 2.1]
      : isRoadFamily(kind)
        ? [ROAD_SPECS[kind].width, ROAD_SPECS[kind].length, ROAD_SPECS[kind].height]
        : kind === 'train'
          ? [2.6, 11.6, 3.2]
          : kind === 'fire' || kind === 'tow'
            ? [2.1, 6.3, 2.7]
            : kind === 'ambulance'
              ? [1.8, 4.7, 2.2]
              : kind === 'boat'
                ? [2, 4.5, 1.5]
                : [1.7, 3.8, 1.6];
const angleDelta = (a: number, b: number): number => Math.atan2(Math.sin(b - a), Math.cos(b - a));
/** Persistent actors and local decisions. Traffic owns stable bodies; disabled traffic is dynamic. */
export class Population {
  naval?: Naval;
  invasion?: Invasion;
  powerPlant?: PowerPlant;
  military?: Military;
  shelters?: Shelters;
  drivers?: CivilianDrivers;
  coastStaff?: CoastStaff;
  civil?: CivilLife;
  services?: Services;
  rail?: Railway;
  aviation?: Aviation;
  airstrikes?: Airstrikes;
  marine?: Marine;
  airSupport?: AirSupport;
  buses?: Buses;
  logistics?: Logistics;
  shopStaff?: ShopStaff;
  cutaway?: { building: number; floor: number };
  private actorVisible(actor: Actor): boolean {
    if (actor.hidden || actor.streamedOut) return false;
    if (!this.cutaway || !actor.layer?.startsWith('interior:')) return true;
    const parts = actor.layer?.split(':');
    return !(
      this.cutaway &&
      parts?.[0] === 'interior' &&
      Number(parts[1]) === this.cutaway.building &&
      Number(parts[2]) > this.cutaway.floor
    );
  }
  people: Person[] = [];
  cars: TrafficVehicle[] = [];
  animals: Animal[] = [];
  hazards: Hazard[] = [];
  density = 220;
  reactions = 0;
  waiting = 0;
  viewFocus: { x: number; z: number } = { x: -55, z: -40 };
  viewCamera?: THREE.Camera;
  presentationHeight = 1440;
  viewLight?: THREE.DirectionalLight;
  private interiorOcclusion: InteriorOcclusion;
  private lightDirection = new THREE.Vector3();
  private occludedResidents = 0;
  private viewFrustum = new THREE.Frustum();
  private viewProjection = new THREE.Matrix4();
  private viewSphere = new THREE.Sphere();
  private submittedResidents = 0;
  private submittedCrew = 0;
  readonly network: RoadNetwork;
  readonly crossings = new CrossingControl();
  private meshes = new Map<string, THREE.InstancedMesh>();
  private byBody = new Map<number, TrafficVehicle>();
  private junctions = new Map<number, TrafficVehicle>();
  private junctionAttempt = new Map<number, number>();
  private targetMatrices = new Map<string, THREE.Matrix4>();
  private targetsDirty = true;
  private poseStamp = 0;
  private renderAlpha = 1;
  private actorIndex = new Spatial<Actor>();
  private footIndex = new Spatial<Person>(2);
  private dangerIndex = new Spatial<Hazard>(24);
  private responses = new Map<string, Response>();
  private seen = new Map<string, Set<string>>();
  private wakePool: Wake[] = [];
  private wakes: THREE.InstancedMesh;
  private hoses: THREE.LineSegments<THREE.BufferGeometry, THREE.LineBasicMaterial>;
  private hosePositions = new Float32Array(24);
  private dummy = new THREE.Object3D();
  private color = new THREE.Color();
  private random = seeded(82);
  private clock = 0;
  private indexAge = 0;
  private responseAge = 0;
  private wakeAge = 0;
  private fireHazards: Hazard[] = [];
  private bodyWorld?: RAPIER.World;
  private visible = true;
  private directImpacts = 0;
  private reroutes = 0;
  private dispatches = 0;
  private extinguished = 0;
  private resolved = 0;
  private waterApplied = 0;
  private animalReactions = 0;
  private navigationChecks = 0;
  private stoppedTrafficReuses = 0;
  private footRouteSteps = 0;
  private footFreeSteps = 0;
  private footSteeringChecks = 0;
  private footSteeringWaits = 0;
  private footFlatSteps = 0;
  private personSerial = 0;
  readonly characters: CharacterRenderer;
  private water?: WaterSystem;
  constructor(
    readonly physics: PhysicsCity,
    readonly city: City,
    readonly fire: FireSystem,
    readonly effects: Effects,
    readonly audio: AudioSystem,
    readonly entities?: EntityRegistry,
  ) {
    this.interiorOcclusion = new InteriorOcclusion(physics);
    this.network = new RoadNetwork(city, physics);
    this.characters = new CharacterRenderer(physics.scene, physics.material, city.seed);
    for (const sign of [-1, 1]) this.addMesh(`train-door:${sign}`, trainDoorGeometry(sign), 16);
    this.addMesh('road-wheel', roadWheelGeometry(), 3600);
    this.addMesh('delivery-door', deliveryDoorGeometry(), 128);
    for (const kind of ['bus', 'coach'] as const)
      for (const sign of [-1, 1]) this.addMesh(`${kind}-door:${sign}`, busDoorGeometry(kind, sign), 64);
    for (const kind of [
      ...ROAD_FAMILIES,
      ...MILITARY_FAMILIES,
      'train',
      'police',
      'fire',
      'boat',
      'ambulance',
      'tow',
      'airport-fuel',
      'airport-baggage',
    ] as VehicleKind[])
      this.addMesh(kind, vehicleGeometry(kind), 384);
    for (const kind of MILITARY_FAMILIES)
      if (MILITARY_SPECS[kind].gun) this.addMesh(`turret:${kind}`, militaryTurretGeometry(kind), 128);
    for (const kind of ['sheep', 'cow', 'bird'] as const) this.addMesh(kind, animalGeometry(kind), 64);
    for (const family of ['pigeon', 'gull'] as const) {
      this.addMesh(`bird:${family}`, animalGeometry('bird', family), 32);
      for (const side of [-1, 1])
        this.addMesh(`bird-wing:${family}:${side}`, birdWingGeometry(family, side), 32);
    }
    for (const family of RENDER_BOAT_FAMILIES)
      this.addMesh(`boat:${family}`, boatGeometry(family), family === 'landing' ? 16 : 8);
    this.addMesh('crew', personGeometry(true, 1), 8);
    const cones = new THREE.ConeGeometry(0.24, 0.65, 5);
    cones.translate(0, 0.325, 0);
    this.addMesh('cones', cones, 32, new THREE.MeshStandardMaterial({ color: 0xe5974c, roughness: 1 }));
    this.addMesh(
      'carry',
      new THREE.BoxGeometry(0.26, 0.34, 0.2),
      2000,
      new THREE.MeshStandardMaterial({ color: 0xb8996b, roughness: 1 }),
    );
    const wakeGeometry = new THREE.RingGeometry(0.83, 1, 20, 1, 0, Math.PI);
    wakeGeometry.rotateX(-Math.PI / 2);
    this.wakes = new THREE.InstancedMesh(
      wakeGeometry,
      new THREE.MeshBasicMaterial({
        color: 0xd5e8e0,
        transparent: true,
        opacity: 0.2,
        depthWrite: false,
        side: THREE.DoubleSide,
      }),
      64,
    );
    this.wakes.count = 0;
    this.wakes.frustumCulled = false;
    physics.scene.add(this.wakes);
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(this.hosePositions, 3));
    geometry.setDrawRange(0, 0);
    this.hoses = new THREE.LineSegments(
      geometry,
      new THREE.LineBasicMaterial({ color: 0xc5e4ed, transparent: true, opacity: 0.74 }),
    );
    this.hoses.frustumCulled = false;
    physics.scene.add(this.hoses);
  }
  private addMesh(
    key: string,
    geometry: THREE.BufferGeometry,
    count: number,
    material: THREE.Material | THREE.Material[] = this.physics.material,
  ): void {
    const mesh = new THREE.InstancedMesh(geometry, material, count);
    mesh.count = 0;
    mesh.castShadow = mesh.receiveShadow = true;
    mesh.frustumCulled = false;
    mesh.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.physics.scene.add(mesh);
    this.meshes.set(key, mesh);
  }
  reset(seed: number, density = this.density, restoredCity = false): void {
    for (const step of this.resetSteps(seed, density, restoredCity)) void step;
  }
  *resetSteps(seed: number, density = this.density, restoredCity = false): Generator<void> {
    yield* this.clearSteps();
    this.bodyWorld = this.physics.world;
    this.random = seeded(seed + 82);
    this.density = Math.max(0, Math.min(this.city.regional ? 2000 : 300, Math.round(density)));
    const edges = [...this.network.edges.values()].filter(
        (e) => e.kind !== 'airport service' && !e.restricted,
      ),
      urban = edges.filter((e) => {
        const p = this.network.nodes.get(e.from)!;
        return this.city.regional ? Math.hypot(p.x + 45, p.z + 30) < 220 : p.x < 65 && p.z < 100;
      });
    for (let i = 0; i < this.density; i++) {
      const list = i < this.density * 0.7 && urban.length ? urban : edges;
      if (!list.length) break;
      const edge = list[Math.floor(this.random() * list.length)],
        from = i % 2 ? edge.from : edge.to,
        to = this.network.other(edge, from);
      const position = this.clearFootpath(
          this.network.waypoint(edge, from, 0.1 + this.random() * 0.8, true),
          edge,
          from,
        ),
        a = this.base(`person-${i}`, 'person', position);
      a.name = `Resident ${i + 1}`;
      a.variant = i % 8;
      a.scale = 0.91 + this.random() * 0.18;
      a.color = PALETTE[i % 8];
      a.speed = 0.8 + this.random() * 0.65;
      this.people.push({
        ...a,
        category: 'person',
        state: i % 7 === 0 ? 'idle' : 'walk',
        timer: 2 + this.random() * 10,
        from,
        to,
        edge: edge.id,
        group: Math.floor(i / 3),
      });
      if (i % 16 === 15) yield;
    }
    // Legacy compact fixtures still have roadside service slots. Reserve these
    // actual bodies before ordinary placement so a bus cannot be born across
    // a stationary engine. The regional city uses its represented facilities.
    const civic = this.city.buildings.filter((b) => b.kind === 'civic');
    for (let i = 0; i < (this.city.facilities?.length ? 0 : 4) && edges.length; i++) {
      const station = civic[Math.min(civic.length - 1, Math.floor(i / 2) * 2)],
        node = this.network.nearest(station ? { x: station.x, z: station.z } : { x: -100 + i * 65, z: 30 }),
        adjacent = this.network.adjacent.get(node)?.[0];
      if (!adjacent) continue;
      const t = 0.42 + (i % 2) * 0.22,
        p = this.network.waypoint(adjacent, node, t),
        forward = this.network.waypoint(adjacent, node, t + 0.01),
        car = this.createCar(`responder-${i}`, i % 2 ? 'police' : 'fire', p, node);
      car.yaw = Math.atan2(p.x - forward.x, p.z - forward.z);
      car.q.setFromAxisAngle(Y_AXIS, car.yaw);
      car.previousQ.copy(car.q);
      car.body.setRotation(car.q, true);
      car.state = 'station';
      car.station = node;
      car.home.copy(car.p);
      car.name = i % 2 ? 'Police unit' : 'Fire engine';
      yield;
    }
    const ordinary = this.city.ordinaryTraffic ?? Math.max(12, Math.round(this.density * 0.2)),
      kinds: VehicleKind[] = this.city.ordinaryTraffic
        ? [...ROAD_FAMILIES]
        : ['car', 'car', 'van', 'car', 'bus', 'motorcycle', 'truck', 'car'];
    for (let i = 0; i < ordinary && edges.length; i++) {
      const selected = kinds[i % kinds.length],
        kind =
          this.city.streetTransit && i >= 64 && (selected === 'bus' || selected === 'coach')
            ? 'car'
            : selected,
        candidateEdges = i < ordinary * 0.7 && urban.length ? urban : edges,
        list = candidateEdges.filter((e) => {
          const a = this.network.nodes.get(e.from)!,
            b = this.network.nodes.get(e.to)!;
          const span = e.profile ? roadLength(e) : Math.hypot(a.x - b.x, a.z - b.z);
          return (
            dimensions(kind)[1] <= (e.maxVehicleLength ?? Infinity) &&
            (e.curved ||
              span >
                this.junctionStopRadius(e.from, dimensions(kind)[1]) +
                  this.junctionStopRadius(e.to, dimensions(kind)[1]) +
                  0.5)
          );
        });
      if (!list.length) throw new Error(`No road can safely hold the requested ${kind} fleet`);
      let edge = list[(i * 17 + 3) % list.length],
        from = i % 2 ? edge.from : edge.to,
        t = 0.13 + ((i * 0.237) % 0.72),
        p = this.network.waypoint(edge, from, t);
      const [width, length] = dimensions(kind);
      let placed = false;
      for (let attempt = 0; attempt < list.length * 4; attempt++) {
        edge = list[(i * 17 + 3 + attempt) % list.length];
        from = (i + Math.floor(attempt / list.length)) % 2 ? edge.from : edge.to;
        const a = this.network.nodes.get(from)!,
          b = this.network.nodes.get(this.network.other(edge, from))!;
        const span = edge.profile ? roadLength(edge) : Math.hypot(a.x - b.x, a.z - b.z);
        const start = edge.curved ? 0.16 : (this.junctionStopRadius(from, length) + 0.2) / span;
        const end = edge.curved ? 0.84 : 1 - (this.junctionStopRadius(b.id, length) + 0.2) / span;
        t = start + ((i * 0.237 + attempt * 0.157) % 1) * (end - start);
        p = this.network.waypoint(edge, from, t);
        const ahead = this.network.waypoint(edge, from, Math.min(1, t + 0.03));
        const yaw = Math.atan2(p.x - ahead.x, p.z - ahead.z);
        if (
          !this.cars.some(
            (c) =>
              Math.abs(c.p.y - p.y) < 2 &&
              overlapsRoadBody(
                { ...p, yaw, width: width + 0.6, length: length + 6 },
                { x: c.p.x, z: c.p.z, yaw: c.yaw, width: c.width, length: c.length },
              ),
          )
        ) {
          placed = true;
          break;
        }
      }
      if (!placed) throw new Error(`No collision-free initial road slot for traffic-${i}`);
      const car = this.createCar(`traffic-${i}`, kind, p, from);
      car.node = this.network.other(edge, from);
      car.previousNode = from;
      car.plan = this.network.points(edge, from, t);
      car.yaw = Math.atan2(-(car.plan[0].x - p.x), -(car.plan[0].z - p.z));
      car.q.setFromAxisAngle(Y_AXIS, car.yaw);
      car.body.setRotation(car.q, true);
      car.previousQ.copy(car.q);
      car.state = 'driving';
      if (i % 11 === 0 && kind !== 'bus' && kind !== 'coach') {
        const curb = this.network.waypoint(edge, from, t, true);
        const outward = new THREE.Vector3(curb.x - p.x, 0, curb.z - p.z).normalize();
        const bay = new THREE.Vector3(curb.x, curb.y, curb.z).addScaledVector(outward, 2.5);
        const ground = this.physics.ground(bay, 0.35, 0.6);
        if (
          ground &&
          !this.physics.blocked(bay.x, bay.z, car.width * 0.6, ground.y + 1, car.body) &&
          !this.cars.some(
            (other) =>
              other !== car &&
              Math.abs(other.p.y - ground.y) < 2 &&
              overlapsRoadBody(
                { x: bay.x, z: bay.z, yaw: car.yaw, width: car.width, length: car.length },
                { x: other.p.x, z: other.p.z, yaw: other.yaw, width: other.width, length: other.length },
                0.2,
              ),
          )
        ) {
          car.state = 'parked';
          bay.y = ground.y;
          car.p.copy(bay);
          car.previous.copy(bay);
          car.body.setTranslation(bay, true);
        }
      }
      yield;
    }
    yield;
    const rural = this.city.districts?.find((d) => d.id === 'rural') ?? { x: 150, z: 145 };
    for (let i = 0; i < 32; i++) {
      const kind = i % 3 ? 'sheep' : 'cow',
        pastureIndex = Math.floor(i / 8),
        pasture = this.city.habitats?.pastures[pastureIndex],
        jitterX = (this.random() - 0.5) * 14,
        jitterZ = (this.random() - 0.5) * 14,
        home = pasture
          ? new THREE.Vector3(pasture.x, GROUND, pasture.z)
          : new THREE.Vector3(rural.x + (i < 16 ? -18 : 21), GROUND, rural.z + (i < 16 ? -13 : 15)),
        p = pasture
          ? new THREE.Vector3().copy(pasture.grazings[(i % 8) * 3])
          : home.clone().add(new THREE.Vector3(jitterX, 0, jitterZ)),
        a = this.base(`animal-${i}`, 'animal', p);
      a.home.copy(home);
      a.name = kind === 'cow' ? 'Meadow cow' : 'Meadow sheep';
      a.scale = 0.85 + this.random() * 0.25;
      a.speed = kind === 'cow' ? 0.7 : 1;
      a.state = 'graze';
      this.animals.push({
        ...a,
        category: 'animal',
        kind,
        pasture: pasture ? pastureIndex : undefined,
        thirst: 18 + (i % 8) * 7,
        drinks: 0,
        rests: 0,
        restDue: 25 + (i % 8) * 4,
      });
    }
    const trees = (this.city.props ?? []).filter((b) => b.kind === 'tree');
    for (let i = 0; i < 24; i++) {
      const birdFamily: BirdFamily = i >= 16 ? 'gull' : 'pigeon',
        homes =
          birdFamily === 'gull' && this.city.coast
            ? trees.filter((t) => t.z > 520)
            : trees.filter((t) => Math.abs(t.x) < 220 && Math.abs(t.z) < 220),
        choices = homes.length ? homes : trees,
        tree = choices[(i * 7) % Math.max(1, choices.length)],
        cell = tree ? [...tree.cells].sort((a, b) => b.y - a.y)[0] : undefined,
        home =
          (cell ? this.physics.cellPosition(cell.id)?.add(new THREE.Vector3(0, 0.65, 0)) : undefined) ??
          new THREE.Vector3(rural.x, 8, rural.z),
        a = this.base(`bird-${i}`, 'animal', home);
      a.home.copy(home);
      a.name = birdFamily === 'gull' ? 'Coastal gull' : 'City pigeon';
      a.speed = 4.5 + this.random() * 2;
      a.state = i % 3 && cell ? 'perch' : 'fly';
      a.goal.copy(home).add(new THREE.Vector3(8, 5, 8));
      a.timer = 8 + (i % 8) * 2;
      this.animals.push({
        ...a,
        category: 'animal',
        kind: 'bird',
        birdFamily,
        flock: Math.floor(i / 8),
        perch: cell?.id,
      });
    }
    const river = this.city.river?.points ?? [];
    for (let i = 0; i < 4 && river.length > 2; i++) {
      const next = Math.floor(((i + 1) * river.length) / 5),
        point = river[next],
        family = BOAT_FAMILIES[i],
        dock = this.city.marina?.docks[i < 2 ? 0 : 1],
        slot = dock?.slots[i % 2],
        spawn = slot
          ? {
              x: slot.edge + slot.side * (BOAT_SPECS[family].width / 2 + 0.3),
              y: WATER_LEVEL - 0.565,
              z: slot.z,
            }
          : { x: point.x + (i % 2 ? 3 : -3), y: 0, z: point.z },
        boat = this.createCar(`boat-${i}`, 'boat', spawn, 0, family);
      boat.riverNext = next + 1;
      boat.riverSign = i % 2 ? -1 : 1;
      boat.speed = 2.5 + i * 0.3;
      boat.state = 'sailing';
      boat.name = [
        'Linden passenger ferry',
        'Linden packet workboat',
        'Seabreeze river launch',
        'Coastal fishing skiff',
      ][i];
    }
    this.physics.world.propagateModifiedBodyPositionsToColliders();
    this.personSerial = this.people.length;
    this.refreshSpatial();
    yield* this.refreshEntityTargetSteps();
    if (this.civil) yield* this.civil.resetSteps(restoredCity);
    if (this.services) yield* this.services.resetSteps();
    yield;
    this.rail?.reset();
    yield;
    this.aviation?.reset();
    this.airstrikes?.clear();
    yield;
    this.marine?.reset();
    yield;
    this.airSupport?.reset();
    yield;
    this.buses?.reset();
    yield;
    this.logistics?.reset();
    yield;
    this.shopStaff?.reset();
    yield;
    this.coastStaff?.reset();
    yield;
    this.powerPlant?.reset();
    yield;
    if (this.military) yield* this.military.resetSteps();
    if (this.drivers) yield* this.drivers.resetSteps();
    this.shelters?.reset();
    this.invasion?.reset();
    yield* this.refreshEntityTargetSteps();
    this.render(1);
  }
  /** A population setting never resets wrecks, casualties, wildlife or responders. */
  setDensity(density: number): void {
    this.targetsDirty = true;
    this.density = Math.max(0, Math.min(this.city.regional ? 2000 : 300, Math.round(density)));
    let residents = this.people.filter((p) => !p.id.startsWith('crew:')).length;
    for (let i = this.people.length - 1; i >= 0 && residents > this.density; i--) {
      if (
        !this.people[i].id.startsWith('crew:') &&
        !['incapacitated', 'falling'].includes(this.people[i].state) &&
        !this.people[i].controlled
      ) {
        this.civil?.remove(this.people[i]);
        this.people.splice(i, 1);
        residents--;
      }
    }
    const edges = [...this.network.edges.values()].filter(
      (e) => e.kind !== 'airport service' && !e.restricted,
    );
    while (residents < this.density && edges.length) {
      const id = this.personSerial++;
      const edge = edges[Math.floor(this.random() * edges.length)];
      const from = id % 2 ? edge.from : edge.to;
      const p = this.clearFootpath(
        this.network.waypoint(edge, from, 0.1 + this.random() * 0.8, true),
        edge,
        from,
      );
      const actor = this.base(`person-${id}`, 'person', p);
      actor.name = `Resident ${id + 1}`;
      actor.variant = id % 8;
      actor.color = PALETTE[id % 8];
      actor.scale = 0.91 + this.random() * 0.18;
      actor.speed = 0.8 + this.random() * 0.65;
      this.people.push({
        ...actor,
        category: 'person',
        from,
        to: this.network.other(edge, from),
        edge: edge.id,
        group: Math.floor(id / 3),
      });
      this.civil?.enroll(this.people.at(-1)!);
      residents++;
    }
    this.refreshSpatial();
    this.render(1);
  }
  private refreshEntityTargets(): void {
    for (const step of this.refreshEntityTargetSteps()) void step;
  }
  private *refreshEntityTargetSteps(): Generator<void> {
    if (!this.targetsDirty) return;
    this.targetsDirty = false;
    this.characters.retain(new Set(this.people.map((p) => p.id)));
    if (!this.entities) return;
    const actors = [...this.people, ...this.animals, ...this.cars],
      live = new Set(actors.map((a) => a.id));
    for (const id of this.targetMatrices.keys())
      if (!live.has(id)) {
        this.entities.unregister(id);
        this.targetMatrices.delete(id);
      }
    let registered = 0;
    for (const actor of actors) {
      if (this.targetMatrices.has(actor.id)) continue;
      const kind =
        actor.category === 'person'
          ? `person-${actor.variant}`
          : actor.category === 'animal'
            ? (actor as Animal).kind
            : (actor as TrafficVehicle).kind;
      const character = actor.category === 'person' ? this.characters.ensure(actor.id) : undefined;
      const family = actor.category === 'vehicle' ? (actor as TrafficVehicle).boatFamily : undefined;
      const bird = actor.category === 'animal' ? (actor as Animal).birdFamily : undefined;
      const geometry = character
        ? undefined
        : this.meshes.get(family ? `boat:${family}` : bird ? `bird:${bird}` : kind)!.geometry;
      if (geometry && !geometry.boundingBox) geometry.computeBoundingBox();
      const matrix = new THREE.Matrix4().makeTranslation(actor.p.x, actor.p.y, actor.p.z);
      const armedCar =
        actor.category === 'vehicle' && isMilitaryVehicle(actor.kind) && MILITARY_SPECS[actor.kind].gun
          ? actor
          : undefined;
      const turretBounds = armedCar
        ? this.meshes.get(`turret:${armedCar.kind}`)!.geometry.boundingBox!
        : undefined;
      const bounds = character ? this.characters.bounds.clone() : geometry!.boundingBox!.clone();
      if (armedCar && turretBounds) {
        const radius = Math.max(
          Math.abs(turretBounds.min.z),
          Math.abs(turretBounds.max.z),
          Math.abs(turretBounds.min.x),
          Math.abs(turretBounds.max.x),
        );
        bounds.expandByPoint(new THREE.Vector3(-radius, 0, -radius));
        bounds.expandByPoint(
          new THREE.Vector3(
            radius,
            MILITARY_SPECS[armedCar.kind as keyof typeof MILITARY_SPECS].gun + turretBounds.max.y,
            radius,
          ),
        );
      }
      if (character) character.root.copy(matrix);
      this.targetMatrices.set(actor.id, matrix);
      this.entities.register({
        id: actor.id,
        name: actor.name,
        category: actor.category,
        kind: actor.category === 'person' ? 'pedestrian' : kind,
        bounds,
        parts:
          character?.bounds ??
          (armedCar
            ? [
                { anchor: 'root', bounds: geometry!.boundingBox! },
                { anchor: 'turret', bounds: turretBounds! },
              ]
            : undefined),
        pose: (anchor) => {
          if (actor.category === 'person') {
            if (actor.targetPoseStamp !== this.poseStamp)
              this.prepareCharacter(actor, this.interpolation(actor, this.renderAlpha), false);
            return this.characters.pose(actor.id, anchor);
          }
          if (armedCar && anchor === 'turret') return this.militaryTurretPose(armedCar, this.renderAlpha);
          return this.targetMatrices.get(actor.id);
        },
        broadPhase:
          actor.category === 'person'
            ? {
                position: actor.p,
                radius: () => 3 * actor.scale + actor.p.distanceTo(actor.previous),
              }
            : undefined,
        body: actor.category === 'vehicle' ? (actor as TrafficVehicle).body : undefined,
        interactable: () => this.visible && this.actorVisible(actor),
      });
      if (++registered % 16 === 0) yield;
    }
  }
  private base(id: string, category: Actor['category'], point: Vec): Actor {
    const p = new THREE.Vector3(point.x, point.y, point.z);
    return {
      id,
      name: id,
      category,
      p,
      previous: p.clone(),
      velocity: new THREE.Vector3(),
      yaw: this.random() * Math.PI * 2,
      state: 'walk',
      timer: 2 + this.random() * 4,
      think: this.random() * 0.15,
      speed: 1,
      color: 0xffffff,
      variant: 0,
      scale: 1,
      goal: p.clone(),
      home: p.clone(),
    };
  }
  private clearFootpath(point: RoutePoint, edge: RoadEdge, from: number): THREE.Vector3 {
    const p = new THREE.Vector3(point.x, point.y, point.z);
    if (!this.physics.blocked(p.x, p.z, 0.27, p.y + 0.8)) return p;
    for (let i = 0; i < 12; i++) {
      const alt = this.network.waypoint(edge, from, 0.05 + this.random() * 0.9, true);
      if (!this.physics.blocked(alt.x, alt.z, 0.27, alt.y + 0.8))
        return new THREE.Vector3(alt.x, alt.y, alt.z);
    }
    return p;
  }
  createCar(
    id: string,
    kind: VehicleKind,
    point: Vec,
    node: number,
    boatFamily?: BoatFamily,
  ): TrafficVehicle {
    const spec = boatFamily ? BOAT_SPECS[boatFamily] : undefined;
    const [width, length, height] = spec ? [spec.width, spec.length, spec.height] : dimensions(kind),
      a = this.base(id, 'vehicle', point),
      body = this.physics.world.createRigidBody(
        RAPIER.RigidBodyDesc.kinematicPositionBased().setTranslation(point.x, point.y, point.z),
      );
    const collider = this.physics.world.createCollider(
      RAPIER.ColliderDesc.cuboid(width * 0.5, kind === 'boat' ? 0.35 : height * 0.42, length * 0.47)
        .setTranslation(0, kind === 'boat' ? 0.53 : height * 0.5, 0)
        .setDensity(kind === 'motorcycle' ? 1.3 : 2.2)
        .setFriction(0.8)
        .setRestitution(0.05)
        .setActiveEvents(RAPIER.ActiveEvents.COLLISION_EVENTS),
      body,
    );
    const car: TrafficVehicle = {
      ...a,
      category: 'vehicle',
      name: isMilitaryVehicle(kind)
        ? MILITARY_SPECS[kind].name
        : isRoadFamily(kind)
          ? ROAD_SPECS[kind].name
          : `${kind[0].toUpperCase()}${kind.slice(1)} vehicle`,
      kind,
      boatFamily,
      // Match the newly created body's identity rotation. Random actor yaw
      // previously gave parked service vehicles an invisible rotated envelope.
      yaw: 0,
      body,
      collider,
      previousQ: new THREE.Quaternion(),
      q: new THREE.Quaternion(),
      health: 1,
      heat: 0,
      exploded: false,
      node,
      previousNode: node,
      station: node,
      plan: [],
      targetSpeed: 0,
      actualSpeed: 0,
      blockedFor: 0,
      blockedBy: '',
      length,
      width,
      speed: kind === 'bus' ? 4.8 : kind === 'truck' ? 4.3 : 6 + this.random() * 2,
      riverNext: 1,
      riverSign: 1,
    };
    car.color =
      kind === 'fire'
        ? 0xc67c68
        : kind === 'police'
          ? 0xb6c9d3
          : kind === 'bus'
            ? 0x86ada9
            : PALETTE[this.cars.length % 8];
    this.cars.push(car);
    if (boatFamily === 'landing') {
      // The wheelhouse is real occupied geometry, while the open passenger
      // well remains clear. All parts share this boat's one rigid body.
      for (const [x, y, z, w, h, d] of [
        [0, 2.005, 12.5, 8, 2.25, 7],
        [-3.6, 4.04, 13, 0.2, 1.5, 6],
        [3.6, 4.04, 13, 0.2, 1.5, 6],
        [0, 4.9, 13, 7.6, 0.2, 6.6],
        [-5.65, 0.79, 0, 0.9, 0.18, 2.6],
        [5.65, 0.79, 0, 0.9, 0.18, 2.6],
      ])
        this.physics.world.createCollider(
          RAPIER.ColliderDesc.cuboid(w / 2, h / 2, d / 2).setTranslation(x, y, z),
          body,
        );
    }
    this.targetsDirty = true;
    this.indexAge = 1;
    this.byBody.set(body.handle, car);
    if (kind === 'boat') this.registerBoat(car);
    this.fire.register({
      id: `vehicle:${id}`,
      category: 'vehicle',
      burnSeconds:
        kind === 'motorcycle' ? 120 : kind === 'bus' || kind === 'truck' || kind === 'coach' ? 260 : 180,
      position: () => ({ x: car.p.x, y: car.p.y + 0.8, z: car.p.z }),
      fuel: () => (car.exploded ? 0.22 : car.health <= 0 ? 0.45 : 0.95),
      onHeat: (heat, dt) => {
        car.heat = heat;
        if (heat > 0.62) {
          car.health = Math.max(0, car.health - dt * (heat - 0.6) * 0.085);
          if (
            isMilitaryVehicle(car.kind) || car.boatFamily === 'landing'
              ? heat > 2.4 || car.health <= 0
              : heat > 0.9 || car.health < 0.55
          )
            this.disable(car);
        }
      },
      onExplosion: () => {
        if (car.exploded) return;
        car.exploded = true;
        car.health = 0;
        this.disable(car);
        const p = car.body.translation();
        this.physics.blast({ x: p.x, y: p.y + 0.7, z: p.z }, 7, 6, true, 'vehicle', 1);
        this.effects.burst(p, 0xa29b84, 24, 7);
        this.audio.play('blast', 0.8, p);
      },
    });
    return car;
  }
  createCrew(id: string, point: Vec): Person {
    const node = this.network.nearest(point),
      edge = this.network.adjacent.get(node)?.[0];
    const p: Person = {
      ...this.base(id, 'person', point),
      category: 'person',
      from: node,
      to: edge ? this.network.other(edge, node) : node,
      edge: edge?.id ?? 0,
      group: -1,
      controlled: true,
    };
    p.name = 'Service crew';
    p.color = 0xb8d1c4;
    p.speed = 1.55;
    p.variant = 1;
    this.people.push(p);
    this.targetsDirty = true;
    this.indexAge = 1;
    return p;
  }
  /** A mission may retire an intact vehicle only after its own physical exit.
   * Wrecks remain owned by damage/physics until an explicit full rebuild. */
  retireVehicle(car: TrafficVehicle): boolean {
    if (car.state === 'wreck' || !this.cars.includes(car) || this.people.some((p) => p.carrier === car.id))
      return false;
    this.fire.unregister(`vehicle:${car.id}`);
    if (car.kind === 'boat') this.water?.unregister(`boat:${car.id}`);
    this.byBody.delete(car.body.handle);
    this.physics.world.removeRigidBody(car.body);
    this.cars = this.cars.filter((c) => c !== car);
    this.targetsDirty = true;
    this.indexAge = 1;
    return true;
  }
  retireCrew(person: Person): boolean {
    if (person.fatal || ['falling', 'incapacitated', 'carried'].includes(person.state)) return false;
    this.civil?.remove(person);
    this.people = this.people.filter((p) => p !== person);
    this.targetsDirty = true;
    this.indexAge = 1;
    return true;
  }
  setWater(water: WaterSystem): void {
    this.water = water;
    for (const car of this.cars) if (car.kind === 'boat') this.registerBoat(car);
  }
  private registerBoat(boat: TrafficVehicle): void {
    this.water?.register({
      id: `boat:${boat.id}`,
      body: () => this.byBody.get(boat.body.handle)?.body,
      colliders: () => [boat.collider],
      halfHeight: 0.35,
      halfWidth: boat.width / 2,
      halfLength: boat.length * 0.47,
      centerY: 0.53,
      draft: 0.55,
    });
  }
  private refreshSpatial(): void {
    this.actorIndex.rebuild([...this.people, ...this.cars, ...this.animals]);
    this.footIndex.rebuild(this.people.filter((p) => !p.ambientCoarse));
    this.fireHazards = this.fire.incidents().map((i) => ({
      id: `fire-${i.id}`,
      p: new THREE.Vector3(i.point.x, i.point.y, i.point.z),
      radius: 5 + i.intensity * 6,
      life: 1,
    }));
    this.dangerIndex.rebuild([...this.hazards, ...this.fireHazards]);
    this.indexAge = 0;
  }
  disturb(event: Disturbance): void {
    if (event.kind === 'collapse') this.services?.reportCollapse(event.point, event.radius);
    this.military?.relief.report(event);
    if (this.hazards.length >= 32) this.hazards.shift();
    this.hazards.push({
      id: `disturbance-${this.clock}-${this.hazards.length}`,
      p: new THREE.Vector3(event.point.x, event.point.y, event.point.z),
      radius: Math.min(38, event.radius),
      life: event.kind === 'collapse' ? 8 : 6,
    });
    this.actorIndex.query(event.point, event.radius + 8, (actor) => {
      if (Math.hypot(actor.p.x - event.point.x, actor.p.z - event.point.z) > event.radius) return;
      if (actor.category === 'person') this.flee(actor, event.point, 7);
      else if (actor.category === 'animal') {
        this.flee(actor, event.point, 9);
        this.animalReactions++;
      } else {
        const car = actor as TrafficVehicle;
        if (car.state === 'driving') {
          car.state = 'waiting';
          car.timer = 1.5 + this.random();
          car.targetSpeed = 0;
          this.reactions++;
        }
      }
    });
    this.indexAge = 1;
  }
  damage(event: BlastEvent): void {
    const key = `${event.epoch}:${event.id}`;
    let seen = this.seen.get(key);
    if (!seen) {
      while (this.seen.size >= 96) this.seen.delete(this.seen.keys().next().value!);
      this.seen.set(key, (seen = new Set()));
    }
    this.actorIndex.query(event.point, event.radius + 24, (actor) => {
      let d = Math.hypot(
        actor.p.x - event.point.x,
        actor.p.y + 0.7 - event.point.y,
        actor.p.z - event.point.z,
      );
      if (actor.category === 'vehicle' && (actor as TrafficVehicle).boatFamily === 'landing') {
        const car = actor as TrafficVehicle,
          local = new THREE.Vector3(event.point.x, event.point.y, event.point.z)
            .sub(car.p)
            .applyQuaternion(car.q.clone().invert());
        d = Math.hypot(
          Math.max(0, Math.abs(local.x) - car.width / 2),
          Math.max(0, -local.y, local.y - BOAT_SPECS.landing.height),
          Math.max(0, Math.abs(local.z) - car.length / 2),
        );
      }
      if (d > event.radius || seen!.has(actor.id)) return;
      seen!.add(actor.id);
      if (actor.category === 'person' && (actor as Person).carrier && !(actor as Person).exposedSeat) {
        const carrier = this.cars.find((c) => c.id === (actor as Person).carrier);
        if (carrier && carrier.state !== 'wreck') return;
      }
      const severity =
        event.energy * (event.multiplicity ?? 1) * (0.35 + 0.65 * (1 - d / Math.max(0.01, event.radius)));
      if (actor.category === 'vehicle') {
        const car = actor as TrafficVehicle;
        const armour = isMilitaryVehicle(car.kind)
          ? MILITARY_SPECS[car.kind].armour
          : car.boatFamily
            ? BOAT_SPECS[car.boatFamily].armour
            : undefined;
        car.health = Math.max(
          0,
          car.health - (severity * (armour ? 1 / armour : 0.12)) / (car.protection ?? 1),
        );
        if (armour ? car.health <= 0 : severity > 2.4 || car.health < 0.5) {
          this.disable(car);
          const away = actor.p.clone().sub(new THREE.Vector3(event.point.x, event.point.y, event.point.z));
          if (away.lengthSq() < 0.001) away.set(0.5, 0.2, 0.5);
          away.normalize();
          const force = event.source === 'impact' ? severity / (event.multiplicity ?? 1) : severity;
          const impulse = Math.min(8, force * 1.1) * Math.max(1, car.body.mass());
          car.body.applyImpulse(
            { x: away.x * impulse, y: (0.28 + Math.abs(away.y) * 0.3) * impulse, z: away.z * impulse },
            true,
          );
          car.body.applyTorqueImpulse({ x: force * 0.15, y: force * 0.07, z: force * 0.13 }, true);
        } else {
          car.state = 'waiting';
          car.timer = 2;
          car.targetSpeed = 0;
        }
      } else if (severity > 1.7) {
        if (actor.category === 'person' && (actor as Person).combatHealth !== undefined) {
          const person = actor as Person;
          person.combatHealth = Math.max(0, person.combatHealth! - severity / 1.7);
          if (person.combatHealth > 0) {
            this.flee(actor, event.point, 3);
            return;
          }
        }
        this.incapacitate(actor, event.point, severity);
      } else this.flee(actor, event.point, 8);
    });
  }
  contact(body: RAPIER.RigidBody, point: Vec, energy: number): void {
    const car = this.byBody.get(body.handle);
    if (!car || energy < 2.1) return;
    const armour = isMilitaryVehicle(car.kind)
      ? MILITARY_SPECS[car.kind].armour
      : car.boatFamily
        ? BOAT_SPECS[car.boatFamily].armour
        : undefined;
    car.health = Math.max(
      0,
      car.health - (energy * (armour ? 0.625 / armour : 0.075)) / (car.protection ?? 1),
    );
    if (armour ? car.health <= 0 : energy > 4.5 || car.health < 0.5) {
      this.disable(car);
      const away = car.p
        .clone()
        .sub(new THREE.Vector3(point.x, point.y, point.z))
        .setY(0)
        .normalize();
      car.body.applyImpulse(
        { x: away.x * car.body.mass() * 1.1, y: car.body.mass() * 0.35, z: away.z * car.body.mass() * 1.1 },
        true,
      );
    }
  }
  canAttach(body: RAPIER.RigidBody): boolean {
    return this.byBody.get(body.handle)?.body === body;
  }
  disable(car: TrafficVehicle): void {
    if (car.state === 'wreck') return;
    if (car.junction !== undefined) this.junctions.delete(car.junction);
    car.junction = undefined;
    car.carrier = undefined;
    car.state = 'wreck';
    car.targetSpeed = 0;
    car.plan.length = 0;
    const velocity = { ...car.body.linvel() };
    car.body.setBodyType(RAPIER.RigidBodyType.Dynamic, true);
    car.body.setLinvel(velocity, true);
    car.body.setLinearDamping(car.kind === 'boat' ? 1.2 : 0.7);
    car.body.setAngularDamping(1);
    car.body.enableCcd(true);
    car.collider.setCollisionGroups(0xffffffff);
    for (const cargo of this.cars.filter((c) => c.carrier === car.id)) {
      cargo.carrier = undefined;
      this.disable(cargo);
      cargo.body.setLinvel(velocity, true);
    }
    this.directImpacts++;
  }
  incapacitate(actor: Actor, point: Vec, severity: number): void {
    const away = actor.p.clone().sub(new THREE.Vector3(point.x, point.y, point.z));
    if (away.lengthSq() < 0.01) away.set(0.5, 0, 0.5);
    away.normalize();
    actor.velocity.addScaledVector(away, Math.min(10, severity * 1.3));
    actor.velocity.y = Math.min(5, 1.5 + severity * 0.35);
    actor.state = 'incapacitated';
    actor.timer = Infinity;
    if (actor.category === 'person') this.civil?.suspendInjured(actor as Person);
    this.directImpacts++;
  }
  private flee(actor: Actor, point: Vec, seconds: number): void {
    if (actor.category === 'person' && (actor as Person).carrier) return;
    if (['incapacitated', 'wreck', 'falling'].includes(actor.state)) return;
    const wasFleeing = actor.state === 'flee';
    const away = actor.p.clone().sub(new THREE.Vector3(point.x, actor.p.y, point.z));
    if (away.lengthSq() < 0.01) away.set(1, 0, 0.5);
    away.normalize();
    actor.goal.copy(actor.p).addScaledVector(away, 15);
    actor.goal.x = THREE.MathUtils.clamp(actor.goal.x, -this.city.half + 6, this.city.half - 6);
    actor.goal.z = THREE.MathUtils.clamp(actor.goal.z, -this.city.half + 6, this.city.half - 6);
    actor.state = 'flee';
    actor.timer = seconds;
    actor.think = 0;
    if (!wasFleeing) this.reactions++;
  }
  private decisionDebt = 0;
  update(dt: number): void {
    this.poseStamp++;
    this.clock += dt;
    this.crossings.step(this.clock);
    this.network.time = this.clock;
    this.indexAge += dt;
    this.responseAge += dt;
    this.wakeAge += dt;
    for (let i = this.hazards.length - 1; i >= 0; i--) {
      this.hazards[i].life -= dt;
      if (this.hazards[i].life <= 0) this.hazards.splice(i, 1);
    }
    if (this.indexAge >= 0.25) this.refreshSpatial();
    if (this.responseAge >= 0.5) {
      this.responseAge = 0;
      if (!this.services) this.updateResponses(0.5);
    }
    // High-speed flight and physical ordnance remain fixed-tick. Ground AI
    // consumes accumulated time at 30/20 Hz, with swept movement and rendering
    // interpolation; lowering decision frequency never lowers simulation speed.
    this.airSupport?.update(dt);
    this.rail?.update(dt);
    this.aviation?.update(dt);
    this.airstrikes?.update(dt);
    this.marine?.update(dt);
    this.naval?.update(dt);
    this.decisionDebt += dt;
    const decisionInterval = IS_LITE ? 1 / 20 : 1 / 30;
    if (this.decisionDebt + 1e-6 >= decisionInterval) {
      const elapsed = this.decisionDebt;
      this.decisionDebt = 0;
      this.drivers?.update(elapsed);
      this.shelters?.update(elapsed);
      this.civil?.update(elapsed);
      this.services?.update(elapsed);
      this.buses?.update(elapsed);
      this.logistics?.update(elapsed);
      this.shopStaff?.update(elapsed);
      this.coastStaff?.update(elapsed);
      this.powerPlant?.update(elapsed);
      this.military?.update(elapsed);
      this.invasion?.update(elapsed);
      for (const p of this.people)
        if (p.controlled) {
          this.footIndex.update(p);
          advanceCharacterMotion(
            this.characters.ensure(p.id).motion,
            p.p,
            elapsed,
            p.speed,
            p.state,
            p.hidden,
          );
        }
    }
    const elapsed = (a: Actor) => {
      // Swept controllers accumulate every elapsed second. Physical falling and
      // rigid falls retain 60 Hz; grounded crew/walking/traffic decisions are phased.
      const urgent = ['falling', 'incapacitated', 'carried'].includes(a.state);
      const distant = Math.hypot(a.p.x - this.viewFocus.x, a.p.z - this.viewFocus.z) > (IS_LITE ? 100 : 180);
      const interval = urgent
        ? dt
        : a.category === 'vehicle'
          ? 1 / WORKLOAD.trafficHz
          : a.category === 'person'
            ? distant && a.state !== 'flee'
              ? 0.1
              : 1 / WORKLOAD.pedestrianHz
            : distant
              ? 0.1
              : 1 / 30;
      return actorStep(a, dt, this.clock, interval);
    };
    // Controllers above and vehicle movement below can change rigid bodies.
    // The intervening synchronous pedestrian pass only queries those bodies.
    this.physics.dynamicFootQueries.begin();
    try {
      for (const p of this.people) {
        if (this.stepAmbient(p, dt)) continue;
        if (p.controlled && !['falling', 'incapacitated'].includes(p.state)) {
          p.stepInterval = decisionInterval;
          p.stepDebt = this.decisionDebt;
          continue;
        }
        const delta = elapsed(p);
        if (delta) {
          this.movePerson(p, delta);
          this.footIndex.update(p);
          advanceCharacterMotion(this.characters.ensure(p.id).motion, p.p, delta, p.speed, p.state, p.hidden);
        }
      }
    } finally {
      this.physics.dynamicFootQueries.end();
    }
    for (const a of this.animals) {
      const delta = elapsed(a);
      if (delta) this.moveAnimal(a, delta);
    }
    this.waiting = 0;
    for (const c of this.cars) {
      const delta = elapsed(c);
      if (delta) this.moveCar(c, delta);
    }
    this.military?.syncSeats();
    this.naval?.passengers.syncSeats();
    this.drivers?.syncSeats();
    for (let i = this.wakePool.length - 1; i >= 0; i--) {
      this.wakePool[i].life -= dt;
      if (this.wakePool[i].life <= 0) this.wakePool.splice(i, 1);
    }
    if (this.wakeAge >= 0.3) {
      this.wakeAge = 0;
      for (const boat of this.cars.filter((c) => c.kind === 'boat' && c.state === 'sailing')) {
        if (this.wakePool.length >= 64) this.wakePool.shift();
        this.wakePool.push({
          p: boat.p.clone().add(new THREE.Vector3(Math.sin(boat.yaw) * 2.8, 0, Math.cos(boat.yaw) * 2.8)),
          yaw: boat.yaw,
          life: 4,
        });
      }
    }
  }
  private stepAmbient(p: Person, dt: number): boolean {
    if (!this.city.streetLife) return false;
    const ordinary =
      p.id.startsWith('person-') &&
      !p.controlled &&
      !p.hidden &&
      !p.carrier &&
      !p.fatal &&
      !p.yieldTarget &&
      !this.civil?.routines.has(p.id) &&
      ['walk', 'idle', 'wait'].includes(p.state);
    if (!ordinary) {
      if (p.ambientCoarse) {
        p.footCache = undefined;
        p.previous.copy(p.p);
        this.footIndex.update(p);
      }
      p.ambientCoarse = p.streamedOut = false;
      p.ambientDebt = 0;
      return false;
    }
    if (this.clock >= (p.lodDue ?? 0)) {
      p.lodDue = this.clock + 0.35 + (p.variant % 7) * 0.017;
      const visible = !this.viewCamera || this.inView(p.p, p.ambientCoarse ? 45 : 65);
      const distance = Math.hypot(p.p.x - this.viewFocus.x, p.p.z - this.viewFocus.z);
      const near = distance < (p.ambientCoarse ? 130 : 165);
      const coarse = (!visible || !near) && !this.hazardAt(p.p);
      if (p.ambientCoarse && !coarse) {
        p.footCache = undefined;
        p.previous.copy(p.p);
        this.footIndex.update(p);
      }
      p.ambientCoarse = coarse;
      p.streamedOut = !visible && coarse;
    }
    if (!p.ambientCoarse) return false;
    // Abstract only ordinary distant pavement journeys. Event-driven damage
    // still queries their original positions and promotes them immediately.
    // No rigid bodies, detailed path searches, capsule casts or limb animation.
    const elapsed = actorStep(p, dt, this.clock, 0.5);
    if (!elapsed) return true;
    p.previous.copy(p.p);
    p.timer -= elapsed;
    if (p.state !== 'walk') {
      if (p.timer <= 0) {
        p.state = 'walk';
        p.timer = 12 + this.random() * 22;
      }
      return true;
    }
    if (p.timer <= 0) {
      p.state = 'idle';
      p.timer = 1 + this.random() * 2;
      return true;
    }
    const edge = this.network.edges.get(p.edge);
    if (!edge || !this.network.open(edge, true)) return true;
    const progress = this.network.progress(edge, p.from, p.p);
    const target = this.network.waypoint(edge, p.from, edge.curved ? Math.min(1, progress + 0.025) : 1, true);
    p.goal.set(target.x, target.y, target.z);
    const distance = p.p.distanceTo(p.goal);
    if ((!edge.curved || progress > 0.98) && distance < 1.1) {
      const choices = (this.network.adjacent.get(p.to) ?? []).filter(
        (e) => !e.restricted && e.kind !== 'airport-service' && this.network.open(e, true),
      );
      const onward = choices.filter((e) => e.id !== p.edge),
        options = onward.length ? onward : choices;
      const next = options[Math.floor(this.random() * options.length)];
      if (next) {
        p.from = p.to;
        p.to = this.network.other(next, p.from);
        p.edge = next.id;
      }
    } else if (distance > 0.001) {
      p.yaw = Math.atan2(p.goal.x - p.p.x, p.goal.z - p.p.z);
      p.p.lerp(p.goal, Math.min(1, (p.speed * elapsed) / distance));
    }
    return true;
  }
  private thinkInterval(a: Actor): number {
    return Math.hypot(a.p.x - this.viewFocus.x, a.p.z - this.viewFocus.z) < 150 ? 0.1 : 0.25;
  }
  hazardAt(point: Vec): Vec | undefined {
    let found: Vec | undefined;
    this.dangerIndex.query(point, 40, (h) => {
      if (Math.hypot(h.p.x - point.x, h.p.z - point.z) < h.radius) found = h.p;
    });
    return found;
  }
  evacuate(person: Person, point: Vec): void {
    this.flee(person, point, 7);
  }
  crossingClear(a: Vec, b: Vec): boolean {
    let clear = true;
    this.actorIndex.query(
      { x: (a.x + b.x) / 2, z: (a.z + b.z) / 2 },
      Math.hypot(a.x - b.x, a.z - b.z) / 2 + 12,
      (actor) => {
        if (actor.category !== 'vehicle') return;
        const car = actor as TrafficVehicle;
        if (
          crossesVehicle(a, b, {
            x: car.p.x,
            y: car.p.y,
            z: car.p.z,
            yaw: car.yaw,
            width: car.width,
            length: car.length,
          })
        )
          clear = false;
      },
    );
    return clear;
  }
  footClear(person: Person, target: Vec, from: Vec = person.p): boolean {
    let clear = true;
    const tx = target.x - from.x,
      tz = target.z - from.z,
      length = Math.hypot(tx, tz),
      ux = length ? tx / length : 0,
      uz = length ? tz / length : 0,
      advance = Math.min(0.18, length);
    this.footIndex.query(from, 1.1, (other) => {
      if (
        other.id === person.id ||
        other.category !== 'person' ||
        other.hidden ||
        other.ambientCoarse ||
        ((other as Person).carrier !== undefined && !(other as Person).exposedSeat) ||
        other.state === 'carried' ||
        Math.abs(other.p.y - from.y) > 0.6
      )
        return;
      const dx = other.p.x - from.x,
        dz = other.p.z - from.z,
        along = dx * ux + dz * uz,
        at = Math.max(0, Math.min(advance, along)),
        cx = dx - ux * at,
        cz = dz - uz * at;
      if (cx * cx + cz * cz >= 0.25) return;
      const distance = Math.hypot(dx, dz),
        nextDistance = Math.hypot(dx - ux * advance, dz - uz * advance);
      if (!(distance < 0.5 && nextDistance > distance + 0.001)) clear = false;
    });
    return clear;
  }
  private courtesyProbe = new WeakMap<Person, number>();
  private requestFootClearance(person: Person, target: Vec): void {
    if ((this.courtesyProbe.get(person) ?? 0) > this.clock) return;
    this.courtesyProbe.set(person, this.clock + 0.6);
    const direction = new THREE.Vector3(target.x - person.p.x, 0, target.z - person.p.z).normalize();
    let standing: Person | undefined;
    this.footIndex.query(person.p, 1.1, (a) => {
      if (standing || a.category !== 'person' || a === person) return;
      const other = a as Person;
      if (
        other.hidden ||
        other.controlled ||
        other.fatal ||
        other.carrier ||
        other.yieldTarget ||
        other.layer !== 'street' ||
        Math.abs(other.p.y - person.p.y) > 0.4 ||
        (this.civil?.task(other)?.state !== 'completed' &&
          !(this.city.streetLife && other.id.startsWith('person-') && ['idle', 'wait'].includes(other.state)))
      )
        return;
      const delta = other.p.clone().sub(person.p);
      if (delta.dot(direction) > -0.05 && delta.lengthSq() < 1) standing = other;
    });
    if (!standing) return;
    // A completed pavement interaction must not turn its original occupant
    // into a permanent bollard. Ask that same idle person to take a real,
    // supported step aside; do not push, teleport or erase either pedestrian.
    const other: Person = standing;
    for (const sign of [1, -1]) {
      const goal = other.p
        .clone()
        .add(new THREE.Vector3(-direction.z, 0, direction.x).multiplyScalar(sign * 0.9));
      const floor = this.physics.ground(goal, 0.35, 0.4);
      if (!floor || Math.abs(floor.y - other.p.y) > 0.35 || this.hazardAt(goal)) continue;
      goal.y = floor.y;
      if (!this.footClear(other, goal) || !this.civil?.network.lineOpen(other.p, goal)) continue;
      other.yieldTarget = goal;
      other.yieldSpeed = 1.5;
      other.yieldUntil = this.clock + 2;
      other.detour = undefined;
      return;
    }
  }
  yieldFoot(person: Person, target: Vec, dt: number): void {
    this.requestFootClearance(person, target);
    const direction = new THREE.Vector3(target.x - person.p.x, 0, target.z - person.p.z).normalize();
    // Commit to a short supported sidestep instead of reversing it as soon as
    // the desired heading changes. A packed junction can also need the rear
    // pedestrian to step back before either opposing stream has room to pass.
    for (const angle of [Math.PI / 2, -Math.PI / 2, Math.PI * 0.75, -Math.PI * 0.75, Math.PI]) {
      const side = direction.clone().applyAxisAngle(Y_AXIS, angle),
        goal = person.p.clone().addScaledVector(side, 0.8);
      if (!this.footClear(person, goal)) continue;
      if (
        this.physics.footClearance.obstructed(
          person.p,
          side.clone().multiplyScalar(Math.min(0.8, person.speed * 0.7 * dt)),
          0.24,
          person.footCache,
        )
      )
        continue;
      const floor = this.physics.ground(goal, 0.35, 0.36);
      if (!floor || Math.abs(floor.y - person.p.y) > 0.35) continue;
      goal.y = floor.y;
      if (this.physics.sweepFoot(person.p, goal.clone().sub(person.p)) < 0.999) continue;
      const layer = person.layer;
      const before = person.p.clone();
      person.goal.copy(goal);
      this.moveFoot(person, dt, person.speed * 0.7, true);
      person.layer = layer;
      if (person.p.distanceToSquared(before) > 1e-8) {
        person.yieldTarget = goal;
        person.yieldSpeed = person.speed * 0.7;
        person.yieldUntil = this.clock + 0.8 / person.yieldSpeed + 0.2;
        return;
      }
    }
  }
  walkTo(person: Person, target: Vec, dt: number, speed: number, constrained = false): void {
    const layer = person.layer;
    if (constrained) person.detour = undefined;
    if (person.detour && Math.hypot(person.p.x - person.detour.x, person.p.z - person.detour.z) < 0.15)
      person.detour = undefined;
    if (person.detour) person.goal.copy(person.detour);
    else person.goal.set(target.x, target.y, target.z);
    const before = person.p.clone();
    this.moveFoot(person, dt, speed, true);
    if (!constrained && person.p.distanceToSquared(before) < 1e-7 && person.state !== 'falling') {
      const forward = new THREE.Vector3(target.x - before.x, 0, target.z - before.z).normalize();
      person.detour = undefined;
      const sides = [
        new THREE.Vector3(1, 0, 0),
        new THREE.Vector3(-1, 0, 0),
        new THREE.Vector3(0, 0, 1),
        new THREE.Vector3(0, 0, -1),
      ].sort((a, b) => a.dot(forward) - b.dot(forward));
      for (const side of sides.filter((s) => s.dot(forward) > -0.3)) {
        const candidate = before.clone().addScaledVector(side, 0.65);
        if (!this.footClear(person, candidate)) continue;
        if (
          this.physics.footClearance.obstructed(
            before,
            side.clone().multiplyScalar(Math.min(0.65, speed * dt)),
            0.24,
            person.footCache,
          )
        )
          continue;
        const floor = this.physics.ground(candidate, 0.35, 0.36);
        if (!floor || Math.abs(floor.y - before.y) > 0.35) continue;
        person.goal.copy(candidate);
        this.moveFoot(person, dt, speed, true);
        if (person.p.distanceToSquared(before) > 1e-7) {
          person.detour = candidate;
          break;
        }
      }
    }
    person.layer = layer;
  }
  private dangerAt(_actor: Actor, point: Vec): Hazard | undefined {
    let danger: Hazard | undefined;
    this.dangerIndex.query(point, 40, (h) => {
      if (Math.hypot(h.p.x - point.x, h.p.z - point.z) < h.radius) danger = h;
    });
    return danger;
  }
  private moveIncapacitated(a: Actor, dt: number): void {
    a.previous.copy(a.p);
    const rest = a.resting;
    if (
      rest &&
      a.velocity.lengthSq() < 0.000001 &&
      a.p.x === rest.x &&
      a.p.y === rest.y &&
      a.p.z === rest.z &&
      this.physics.staticSupportVersion(rest.owner) === rest.revision
    )
      return;
    a.resting = undefined;
    a.velocity.y -= 9.8 * dt;
    const next = a.p.clone().addScaledVector(a.velocity, dt);
    if (this.physics.blocked(next.x, next.z, 0.3, next.y + 0.4)) {
      a.velocity.x *= -0.18;
      a.velocity.z *= -0.18;
    } else {
      a.p.x = next.x;
      a.p.z = next.z;
    }
    const drop = Math.max(0.5, a.p.y - next.y + 0.03);
    const support = this.physics.ground(a.p, 0.15, drop);
    let supportY = support?.y,
      carrierSupport = false;
    // Public walking excludes vehicle decks, but a casualty must still land
    // on the actual ship beneath it. Never cache a moving deck as fixed ground.
    for (const ship of this.naval?.ships ?? []) {
      if (Math.hypot(ship.boat.p.x - a.p.x, ship.boat.p.z - a.p.z) > 24) continue;
      const y = this.naval!.deckSupport(ship, a.p, 0.15, drop);
      if (y !== undefined && (supportY === undefined || y > supportY)) {
        supportY = y;
        carrierSupport = true;
      }
    }
    a.p.y = supportY !== undefined && next.y <= supportY ? supportY : next.y;
    if (supportY !== undefined && a.p.y <= supportY + 0.001) {
      a.velocity.y = 0;
      a.velocity.x *= Math.exp(-dt * 5);
      a.velocity.z *= Math.exp(-dt * 5);
      if (!carrierSupport && support && a.velocity.lengthSq() < 0.000001) {
        const revision = this.physics.staticSupportVersion(support.owner);
        if (revision !== undefined) {
          a.velocity.set(0, 0, 0);
          a.resting = { owner: support.owner, revision, x: a.p.x, y: a.p.y, z: a.p.z };
        }
      }
    }
  }
  private movePerson(p: Person, dt: number): void {
    if (p.fatal || ['incapacitated', 'falling', 'carried'].includes(p.state)) this.civil?.suspendInjured(p);
    if (p.fatal && p.state !== 'falling' && p.state !== 'incapacitated') {
      p.state = 'incapacitated';
      p.hidden = false;
      p.controlled = false;
      p.carrier = undefined;
    }
    if (p.state === 'incapacitated') {
      this.moveIncapacitated(p, dt);
      return;
    }
    if (p.state === 'falling') {
      p.previous.copy(p.p);
      p.velocity.y -= 9.8 * dt;
      const support = this.physics.ground(p.p, 0.03, Math.max(0.45, -p.velocity.y * dt + 0.05));
      if (support && p.p.y + p.velocity.y * dt <= support.y) {
        p.p.y = support.y;
        p.velocity.y = 0;
        p.state = p.fatal || (p.fallStart ?? p.p.y) - p.p.y > 1.3 ? 'incapacitated' : 'walk';
        p.fallStart = undefined;
      } else p.p.y += p.velocity.y * dt;
      return;
    }
    if (p.controlled) return;
    if (this.stepYield(p, dt)) return;
    if (this.physics.dangerousRubbleAt(p.p) && this.civil?.routines.has(p.id) && p.think <= 0) {
      const hit = this.physics.world.intersectionWithShape(
        { x: p.p.x, y: p.p.y + 0.75, z: p.p.z },
        { x: 0, y: 0, z: 0, w: 1 },
        new RAPIER.Cuboid(0.26, 0.65, 0.26),
        undefined,
        undefined,
        undefined,
        undefined,
        (col) => {
          const c = this.physics.clusters.get(this.physics.colliderOwner.get(col.handle) ?? -1);
          return !!c && !c.anchored && c.speedBeforeStep > 2.5;
        },
      );
      if (hit) {
        const c = this.physics.clusters.get(this.physics.colliderOwner.get(hit.handle)!)!;
        this.incapacitate(p, c.body.translation(), Math.min(8, c.speedBeforeStep));
        return;
      }
    }
    if (this.civil?.stepPerson(p, dt)) return;
    p.previous.copy(p.p);
    p.timer -= dt;
    p.think -= dt;
    if (p.state === 'flee' && p.timer <= 0) {
      p.state = 'walk';
      p.timer = 5 + this.random() * 8;
    }
    if ((p.state === 'idle' || p.state === 'wait') && p.timer <= 0) {
      p.state = 'walk';
      p.timer = 6 + this.random() * 10;
    }
    if (p.state === 'walk' && p.timer <= 0) {
      p.state = 'idle';
      p.timer = 1 + this.random() * 3;
    }
    if (p.think <= 0) {
      p.think = this.thinkInterval(p);
      this.navigationChecks++;
      const danger = this.dangerAt(p, p.p);
      if (danger && p.state !== 'flee') this.flee(p, danger.p, 6);
      const collider =
        this.physics.dangerousRubbleAt(p.p) &&
        this.physics.world.intersectionWithShape(
          { x: p.p.x, y: p.p.y + 0.75, z: p.p.z },
          { x: 0, y: 0, z: 0, w: 1 },
          new RAPIER.Cuboid(0.26, 0.65, 0.26),
          undefined,
          undefined,
          undefined,
          undefined,
          (col) => {
            const c = this.physics.clusters.get(this.physics.colliderOwner.get(col.handle) ?? -1);
            return !!c && !c.anchored && c.speedBeforeStep > 2.5;
          },
        );
      if (collider) {
        const c = this.physics.clusters.get(this.physics.colliderOwner.get(collider.handle)!)!;
        this.incapacitate(p, c.body.translation(), Math.min(8, c.speedBeforeStep));
        return;
      }
    }
    if (p.state === 'idle' || p.state === 'wait') return;
    if (p.state !== 'flee') {
      const edge = this.network.edges.get(p.edge)!;
      const progress = this.network.progress(edge, p.from, p.p);
      const target = this.network.waypoint(
        edge,
        p.from,
        edge.curved ? Math.min(1, progress + 0.025) : 1,
        true,
      );
      p.goal.set(target.x, target.y, target.z);
      if ((!edge.curved || progress > 0.98) && p.p.distanceTo(p.goal) < 1.1) {
        const choices = (this.network.adjacent.get(p.to) ?? []).filter(
            (e) => e.bridgeId === undefined || this.network.open(e, true),
          ),
          options = choices.filter((e) => e.id !== p.edge),
          next = (options.length ? options : choices)[
            Math.floor(this.random() * (options.length || choices.length))
          ];
        if (next) {
          p.from = p.to;
          p.to = this.network.other(next, p.from);
          p.edge = next.id;
        } else {
          p.state = 'wait';
          p.timer = 2;
        }
        return;
      }
      if (edge.bridgeId !== undefined && !this.network.open(edge, true)) {
        p.state = 'wait';
        p.timer = 3;
        return;
      }
    }
    this.moveFoot(p, dt, p.speed * (p.state === 'flee' ? 2.5 : 1));
  }
  private moveFoot(a: Actor, dt: number, speed: number, followingRoute = false): void {
    const stop = a.steeringStop;
    if (
      !followingRoute &&
      a.state === 'flee' &&
      stop &&
      this.clock < stop.until &&
      a.p.x === stop.x &&
      a.p.y === stop.y &&
      a.p.z === stop.z &&
      a.goal.x === stop.goalX &&
      a.goal.z === stop.goalZ &&
      this.physics.staticSupportVersion(stop.support) === stop.supportRevision &&
      this.physics.footGeometryVersion(a.p) === stop.version
    ) {
      // Only the choice of another direction waits. The parent controller still
      // advances elapsed time and checks hazards/injury at the fixed cadence.
      this.footSteeringWaits++;
      return;
    }
    a.steeringStop = undefined;
    if (
      (a.layer?.startsWith('entrance:') || (followingRoute && Math.abs(a.goal.y - a.p.y) > 0.08)) &&
      speed * dt > 0.045
    ) {
      // A distant 100 ms activity update can otherwise sweep past one narrow
      // tread into the next riser, including the first tread before the route
      // has changed the actor's layer. Integrate the same elapsed motion in bounded
      // capsule checks; the fixed world clock and walking speed stay unchanged.
      const pieces = Math.min(16, Math.ceil((speed * dt) / 0.045));
      for (let i = 0; i < pieces; i++) {
        this.moveFoot(a, dt / pieces, speed, followingRoute);
        if (a.state === 'falling' || a.state === 'incapacitated') break;
      }
      return;
    }
    const dir = a.goal.clone().sub(a.p);
    dir.y = 0;
    const distance = dir.length();
    if (distance < 0.025) return;
    dir.normalize();
    if (followingRoute) this.footRouteSteps++;
    else this.footFreeSteps++;
    const intendedMove = dir.clone().multiplyScalar(Math.min(distance, speed * dt));
    const stepHeight = followingRoute && a.id.startsWith('crew:') ? 0.65 : 0.35;
    if (
      followingRoute &&
      stepHeight <= 0.35 &&
      a.category === 'person' &&
      this.physics.footClearance.obstructed(a.p, intendedMove, 0.24, a.footCache)
    ) {
      if (a.state !== 'flee') {
        a.state = 'wait';
        a.timer = 0.5;
      }
      return;
    }
    // The proof concerns occupied ground, not the source of the destination.
    // Fleeing residents can reuse the same certified straight corridor as an
    // ordinary walker. It is invalidated by local damage, and every actual
    // step still sweeps dynamic obstacles. Stairs, holes and blocked corridors
    // retain the full support/steering solver below.
    const flatCandidate =
      a.category === 'person' && !a.layer?.startsWith('entrance:')
        ? this.physics.footClearance.step(a.p, intendedMove, distance, (a.footCache ??= {}))
        : undefined;
    const flat =
      stepHeight > 0.35 && flatCandidate && flatCandidate.allowed < 0.999 ? undefined : flatCandidate;
    if (flat) this.footFlatSteps++;
    if (!followingRoute && !flat) {
      this.footSteeringChecks++;
      const ahead = a.p.clone().addScaledVector(dir, 0.7);
      const aheadGround = this.physics.ground(ahead, 0.35, 0.48);
      if (
        (!aheadGround || aheadGround.y < a.p.y + 0.07) &&
        this.physics.blocked(ahead.x, ahead.z, 0.28, a.p.y + 0.8)
      ) {
        let clear = false;
        for (const angle of [0.8, -0.8, 1.5, -1.5]) {
          const side = dir.clone().applyAxisAngle(Y_AXIS, angle),
            point = a.p.clone().addScaledVector(side, 0.85);
          if (!this.physics.blocked(point.x, point.z, 0.28, a.p.y + 0.8)) {
            dir.copy(side);
            clear = true;
            break;
          }
        }
        if (!clear) {
          if (a.state !== 'flee') {
            a.state = 'wait';
            a.timer = 1;
          } else this.deferBlockedSteering(a);
          return;
        }
      }
    }
    const move = flat || followingRoute ? intendedMove : dir.multiplyScalar(Math.min(distance, speed * dt)),
      next = a.p.clone().add(move);
    if (
      !followingRoute &&
      a.category === 'person' &&
      this.physics.footClearance.obstructed(a.p, move, 0.24, a.footCache)
    ) {
      if (a.state !== 'flee') {
        a.state = 'wait';
        a.timer = 0.5;
      } else this.deferBlockedSteering(a);
      return;
    }
    let ground = flat ?? this.physics.ground(next, stepHeight, stepHeight + 0.13);
    if (ground && ground.y < a.p.y - 0.08) {
      const trailing = next.clone().addScaledVector(dir.clone().normalize(), -0.24);
      const support = this.physics.ground(trailing, stepHeight, stepHeight + 0.13);
      if (support && support.y > ground.y && support.y <= a.p.y + 0.03) ground = support;
    }
    if (!ground) {
      a.fallStart ??= a.p.y;
      a.velocity.y = 0;
      a.state = 'falling';
      return;
    }
    move.y = ground.y - a.p.y;
    const allowed =
      flat?.allowed ??
      this.physics.sweepFoot(
        a.p,
        move,
        a.category === 'animal' ? 0.32 : 0.24,
        a.category === 'person' ? (a.footCache ??= {}) : undefined,
        stepHeight,
      );
    if (allowed < 0.01) {
      if (a.state !== 'flee') {
        a.state = 'wait';
        a.timer = 0.5;
      } else if (!followingRoute) this.deferBlockedSteering(a);
      return;
    }
    a.yaw = Math.atan2(-move.x, -move.z);
    a.p.addScaledVector(move, allowed);
    a.support = ground.owner;
    if (a.category === 'person' && !followingRoute)
      a.layer = this.network.edges.get((a as Person).edge)?.layer ?? 'street';
  }
  private deferBlockedSteering(a: Actor): void {
    if (a.category !== 'person') return;
    const support = this.physics.ground(a.p, 0.35, 0.48),
      revision = support && this.physics.staticSupportVersion(support.owner);
    if (support && revision !== undefined && Math.abs(support.y - a.p.y) < 0.00005)
      a.steeringStop = {
        x: a.p.x,
        y: a.p.y,
        z: a.p.z,
        goalX: a.goal.x,
        goalZ: a.goal.z,
        until: this.clock + 0.125,
        version: this.physics.footGeometryVersion(a.p),
        support: support.owner,
        supportRevision: revision,
      };
    else if (!support) {
      a.fallStart ??= a.p.y;
      a.velocity.y = 0;
      a.state = 'falling';
    }
  }
  private moveAnimal(a: Animal, dt: number): void {
    if (a.state === 'falling') {
      a.previous.copy(a.p);
      a.velocity.y -= 9.8 * dt;
      const support = this.physics.ground(a.p, 0.03, Math.max(0.45, -a.velocity.y * dt + 0.05));
      if (support && a.p.y + a.velocity.y * dt <= support.y) {
        a.p.y = support.y;
        a.velocity.y = 0;
        a.state = (a.fallStart ?? a.p.y) - a.p.y > 1.3 ? 'incapacitated' : 'roam';
        a.fallStart = undefined;
      } else a.p.y += a.velocity.y * dt;
      return;
    }
    if (a.state === 'incapacitated') {
      this.moveIncapacitated(a, dt);
      return;
    }
    a.previous.copy(a.p);
    a.timer -= dt;
    a.think -= dt;
    if (a.think <= 0) {
      a.think = this.thinkInterval(a);
      const danger = this.dangerAt(a, a.p);
      if (danger && a.state !== 'flee') {
        this.flee(a, danger.p, 9);
        this.animalReactions++;
      }
    }
    if (a.kind === 'bird') {
      this.moveBird(a, dt);
      return;
    }
    const pasture = a.pasture === undefined ? undefined : this.city.habitats?.pastures[a.pasture];
    if (pasture) {
      a.thirst = (a.thirst ?? 30) - dt;
      a.restDue = (a.restDue ?? 25) - dt;
      if (a.waterVersion !== this.physics.structureVersion) {
        a.waterVersion = this.physics.structureVersion;
        a.waterAvailable = this.physics.buildingCells(pasture.water).some((c) => {
          const owner = this.physics.cellOwner.get(c.id);
          return (
            owner !== undefined &&
            (this.physics.clusters.get(owner) ?? this.physics.dormant.get(owner))?.anchored
          );
        });
      }
      const water = a.waterAvailable;
      if (!water && ['to-water', 'drink'].includes(a.state)) {
        a.state = 'graze';
        a.timer = 2;
      }
      if (a.state === 'flee' && a.timer <= 0) {
        a.state = 'graze';
        a.timer = 3;
      }
      if (a.state === 'wait' && a.timer <= 0) {
        a.state = 'graze';
        a.timer = 1;
      }
      if (a.state === 'drink' && a.timer <= 0) {
        a.drinks = (a.drinks ?? 0) + 1;
        a.thirst = 42 + (a.variant % 7) * 3;
        a.state = 'roam';
        a.timer = 18;
        a.goal.copy(pasture.grazings[(a.variant + (a.drinks ?? 0) * 7) % pasture.grazings.length]);
      }
      if (a.state === 'rest' && a.timer <= 0) {
        a.state = 'graze';
        a.timer = 3;
        a.rests = (a.rests ?? 0) + 1;
        a.restDue = 48 + (a.variant % 12);
      }
      if (a.state === 'graze' && a.timer <= 0) {
        if (a.thirst <= 0 && water) {
          const spots = pasture.drinking.filter(
            (point) =>
              !this.animals.some(
                (other) => other !== a && other.p.distanceTo(new THREE.Vector3().copy(point)) < 1.2,
              ),
          );
          if (spots.length) {
            a.goal.copy(spots[a.variant % spots.length]);
            a.state = 'to-water';
            a.timer = 30;
          } else a.timer = 1.5;
        } else if (a.restDue <= 0) {
          a.state = 'rest';
          a.timer = 5 + this.random() * 6;
        } else {
          a.state = 'roam';
          a.timer = 18;
          a.goal.copy(pasture.grazings[Math.floor(this.random() * pasture.grazings.length)]);
        }
      }
      if (a.state === 'to-water' && a.p.distanceTo(a.goal) < 0.24) {
        a.state = 'drink';
        a.timer = 4.5;
        a.yaw = 0;
      }
      if (a.state === 'roam' && (a.p.distanceTo(a.goal) < 0.35 || a.timer <= 0)) {
        a.state = 'graze';
        a.timer = 4 + this.random() * 5;
      }
      if (a.state === 'to-water' && a.timer <= 0) {
        a.state = 'graze';
        a.timer = 2;
      }
      if (['roam', 'flee', 'to-water'].includes(a.state)) {
        const direction = a.goal.clone().sub(a.p).setY(0).normalize(),
          next = a.p.clone().addScaledVector(direction, 0.8);
        if (
          !this.animals.some(
            (other) => other !== a && other.kind !== 'bird' && other.p.distanceTo(next) < 0.8,
          )
        )
          this.moveFoot(a, dt, a.speed * (a.state === 'flee' ? 3.5 : 1));
        else a.timer = Math.min(a.timer, 2);
      }
      return;
    }
    if (a.state === 'flee' && a.timer <= 0) {
      a.state = 'graze';
      a.timer = 1;
    }
    if (a.state === 'wait' && a.timer <= 0) {
      a.state = 'graze';
      a.timer = 1;
    }
    if (a.state === 'graze' && a.timer <= 0) {
      a.state = 'roam';
      a.goal.copy(a.home).add(new THREE.Vector3((this.random() - 0.5) * 15, 0, (this.random() - 0.5) * 15));
      a.timer = 8;
    }
    if (a.state === 'roam' && (a.p.distanceTo(a.goal) < 0.8 || a.timer <= 0)) {
      a.state = 'graze';
      a.timer = 2 + this.random() * 5;
    }
    if (a.state === 'roam' || a.state === 'flee')
      this.moveFoot(a, dt, a.speed * (a.state === 'flee' ? 3.5 : 1));
  }
  private moveBird(a: Animal, dt: number) {
    const perch = this.perchPosition(a);
    if (perch) a.home.copy(perch);
    if ((a.state === 'perch' || a.state === 'landing') && !perch) {
      this.flee(a, a.home, 8);
      this.animalReactions++;
    }
    if (a.state === 'perch' && perch) {
      a.p.copy(perch);
      if (a.timer <= 0) {
        a.state = 'fly';
        a.goal.copy(a.home).add(new THREE.Vector3(5, 5, 5));
        a.timer = 18;
      }
    }
    if (a.state === 'landing' && perch) {
      a.goal.copy(perch);
      if (a.p.distanceTo(perch) < 0.02) {
        a.state = 'perch';
        a.timer = 8 + (a.variant % 8);
      }
    }
    if (a.state === 'flee') {
      a.goal.y = Math.max(a.home.y + 8, 15);
      if (a.timer <= 0) {
        a.state = 'fly';
        a.timer = 8;
      }
    }
    if (!perch && this.clock >= (a.nextPerch ?? 0)) {
      a.nextPerch = this.clock + 8;
      const candidates = (this.city.props ?? [])
        .filter((t) => t.kind === 'tree' && Math.hypot(t.x - a.p.x, t.z - a.p.z) < 90)
        .sort((x, y) => Math.hypot(x.x - a.p.x, x.z - a.p.z) - Math.hypot(y.x - a.p.x, y.z - a.p.z));
      for (const tree of candidates) {
        const cells = this.physics
          .buildingCells(tree.id)
          .filter((c) => {
            const owner = this.physics.cellOwner.get(c.id);
            return (
              owner !== undefined &&
              (this.physics.clusters.get(owner) ?? this.physics.dormant.get(owner))?.anchored
            );
          })
          .sort((x, y) => y.y - x.y);
        if (cells[0]) {
          a.perch = cells[0].id;
          break;
        }
      }
    }
    if (a.state === 'fly' && (a.p.distanceTo(a.goal) < 1 || a.timer <= 0)) {
      const flock = this.animals.filter(
        (b) =>
          b !== a &&
          b.kind === 'bird' &&
          b.flock === a.flock &&
          b.state === 'fly' &&
          b.p.distanceTo(a.p) < 30,
      );
      const centre = flock.length
        ? flock.reduce((p, b) => p.add(b.p), new THREE.Vector3()).multiplyScalar(1 / flock.length)
        : a.home;
      a.goal
        .copy(centre)
        .add(
          new THREE.Vector3((this.random() - 0.5) * 24, 3 + this.random() * 8, (this.random() - 0.5) * 24),
        );
      a.goal.y = Math.max(a.home.y + 3, Math.min(a.home.y + 15, a.goal.y));
      a.timer = 5;
      if (this.random() < 0.15 && perch) {
        a.state = 'landing';
        a.goal.copy(perch);
      }
    }
    if (a.state !== 'perch') {
      const direction = a.goal.clone().sub(a.p).normalize();
      const distance = Math.min(a.p.distanceTo(a.goal), a.speed * (a.state === 'flee' ? 1.6 : 1) * dt);
      for (const turn of [0, 0.75, -0.75, 1.5, -1.5]) {
        const step = direction.clone().applyAxisAngle(Y_AXIS, turn).multiplyScalar(distance);
        if (turn) step.y = Math.max(step.y, distance * 0.5);
        if (
          this.physics.occupiedBoxClear(
            { x: a.p.x, y: a.p.y + 0.21, z: a.p.z },
            { x: 0.22, y: 0.18, z: 0.3 },
            new THREE.Quaternion(),
            step,
          )
        ) {
          a.p.add(step);
          a.yaw = Math.atan2(-step.x, -step.z);
          break;
        }
      }
    }
    return;
  }
  private perchPosition(animal: Animal): THREE.Vector3 | undefined {
    if (animal.perch === undefined) return;
    const owner = this.physics.cellOwner.get(animal.perch);
    if (owner === undefined) return;
    const cluster = this.physics.clusters.get(owner) ?? this.physics.dormant.get(owner);
    if (!cluster?.anchored) return;
    return this.physics.cellPosition(animal.perch)?.add(new THREE.Vector3(0, 0.65, 0));
  }
  private chooseRoad(car: TrafficVehicle): void {
    const choices = (this.network.adjacent.get(car.node) ?? []).filter((e) =>
        this.network.open(e, false, car.length),
      ),
      onward = choices.filter((e) => this.network.other(e, car.node) !== car.previousNode),
      available = onward.length ? onward : choices;
    if (!available.length) {
      car.state = 'waiting';
      car.timer = 2;
      car.targetSpeed = 0;
      return;
    }
    const edge = available[Math.floor(this.random() * available.length)];
    car.previousNode = car.node;
    car.node = this.network.other(edge, car.previousNode);
    car.plan = this.network.points(edge, car.previousNode);
    car.state = 'driving';
  }
  /** Controllers opt into this same physical clearance action. They retain
   * ownership of their person and suspend only that person's conflicting task. */
  stepYield(person: Person, dt: number): boolean {
    if (!person.yieldTarget || person.hidden || ['incapacitated', 'falling', 'flee'].includes(person.state))
      return false;
    person.previous.copy(person.p);
    if (this.clock > (person.yieldUntil ?? 0)) {
      person.yieldTarget = undefined;
      person.yieldSpeed = undefined;
      person.detour = undefined;
      return false;
    }
    person.state = 'yielding';
    if (person.p.distanceTo(person.yieldTarget) >= 0.2 && this.footClear(person, person.yieldTarget))
      this.walkTo(person, person.yieldTarget, dt, person.yieldSpeed ?? 1.5, true);
    return true;
  }
  requestPedestrianClearance(car: TrafficVehicle, allowControlled = false): boolean {
    const person = this.people.find((p) => p.id === car.blockedBy),
      next = car.plan[0];
    if (
      !person ||
      !next ||
      (person.controlled && !allowControlled) ||
      person.hidden ||
      person.yieldTarget ||
      ['incapacitated', 'falling', 'flee'].includes(person.state)
    )
      return false;
    const direction = new THREE.Vector3(next.x - car.p.x, 0, next.z - car.p.z).normalize(),
      side = new THREE.Vector3(-direction.z, 0, direction.x);
    const sign = person.p.clone().sub(car.p).dot(side) >= 0 ? 1 : -1;
    for (const sideSign of [sign, -sign]) {
      const point = person.p.clone().addScaledVector(side, sideSign * (car.width / 2 + 1.7)),
        ground = this.physics.ground(point, 0.35, 0.5);
      if (!ground) continue;
      point.y = ground.y;
      if (
        this.civil
          ? !this.civil.network.lineOpen(person.p, point)
          : this.physics.sweepFoot(person.p, point.clone().sub(person.p)) < 0.99
      )
        continue;
      if (!this.footClear(person, point)) continue;
      person.yieldTarget = point;
      person.yieldSpeed = undefined;
      person.yieldUntil = this.clock + 6;
      person.detour = undefined;
      return true;
    }
    return false;
  }
  reverseForClearance(car: TrafficVehicle, distance = 4): boolean {
    if (!car.plan.length || car.plan[0].reverse || car.plan[0].hold) return false;
    const point = car.p.clone(),
      blockedBy = car.blockedBy,
      reverse = new THREE.Vector3(Math.sin(car.yaw), 0, Math.cos(car.yaw));
    for (let d = 0.25; d <= distance + 0.001; d += 0.25) {
      point.copy(car.p).addScaledVector(reverse, d);
      const ground = this.physics.ground(point, 0.4, 0.65, car.body);
      if (!ground) {
        car.blockedBy = blockedBy;
        return false;
      }
      point.y = ground.y;
      const side = new THREE.Vector3(reverse.z, 0, -reverse.x);
      const structure = [-0.45, 0, 0.45].some((long) =>
        [-0.45, 0, 0.45].some((lat) => {
          const x = point.x + reverse.x * car.length * long + side.x * car.width * lat,
            z = point.z + reverse.z * car.length * long + side.z * car.width * lat;
          return this.physics.blocked(x, z, 0.18, point.y + 1, car.body);
        }),
      );
      if (
        structure ||
        !this.trafficSpaceClear(car, point, car.yaw, car.yaw) ||
        this.fire
          .incidents()
          .some((f) => Math.hypot(f.point.x - point.x, f.point.z - point.z) < 12 + car.width)
      ) {
        car.blockedBy = blockedBy;
        return false;
      }
    }
    car.plan.unshift({ x: point.x, y: point.y, z: point.z, reverse: true, maxSpeed: 1.25, hold: 3 });
    car.blockedBy = '';
    car.think = 0;
    return true;
  }
  private trafficSpaceClear(car: TrafficVehicle, next: Vec, yaw: number, priorYaw: number): boolean {
    let clear = true;
    const cos = Math.abs(Math.cos(yaw)),
      sin = Math.abs(Math.sin(yaw));
    const halfX = (cos * car.width + sin * car.length) * 0.5 + 0.04;
    const halfZ = (sin * car.width + cos * car.length) * 0.5 + 0.04;
    const candidate = { x: next.x, z: next.z, yaw, width: car.width, length: car.length };
    const prior = { ...candidate, x: car.p.x, z: car.p.z, yaw: priorYaw };
    this.actorIndex.query(next, car.length / 2 + 10, (other) => {
      if (
        !clear ||
        other === car ||
        (other.category === 'vehicle' && (other as TrafficVehicle).carrier === car.id) ||
        (other.category === 'person' &&
          (other as Person).carrier !== undefined &&
          !['walk', 'boarding'].includes(other.state)) ||
        other.hidden ||
        Math.abs(other.p.y - next.y) > 2.5 ||
        (other.category === 'animal' && (other as Animal).kind === 'bird')
      )
        return;
      const vehicle = other.category === 'vehicle' ? (other as TrafficVehicle) : undefined;
      // Conservative rotated AABBs reject distant actors in the spatial bin
      // before exact four-axis overlap. No collision envelope is shortened.
      const oc = vehicle ? Math.abs(Math.cos(other.yaw)) : 1,
        os = vehicle ? Math.abs(Math.sin(other.yaw)) : 0;
      const ow = vehicle?.width ?? 0.52,
        ol = vehicle?.length ?? 0.52;
      if (
        Math.abs(other.p.x - next.x) > halfX + (oc * ow + os * ol) * 0.5 ||
        Math.abs(other.p.z - next.z) > halfZ + (os * ow + oc * ol) * 0.5
      )
        return;
      const envelope = {
        x: other.p.x,
        z: other.p.z,
        // A pedestrian's circular foot clearance does not rotate with the
        // animated torso. Use its fixed conservative square for road sweeps.
        yaw: vehicle ? other.yaw : 0,
        width: vehicle?.width ?? 0.52,
        length: vehicle?.length ?? 0.52,
      };
      const penetration = roadBodyPenetration(candidate, envelope, 0.04);
      if (penetration <= 0) return;
      // Existing externally caused overlaps may separate, but normal movement
      // never introduces a new overlap or pushes farther into another actor.
      const priorPenetration = roadBodyPenetration(prior, envelope, 0.04);
      if (priorPenetration > 0 && separatesRoadBody(prior, candidate, envelope, 0.04)) return;
      clear = false;
      car.blockedBy = other.id;
    });
    return clear;
  }
  private junctionSpeed(car: TrafficVehicle, goal: RoutePoint): number {
    const junctionId = goal.junction ?? goal.node;
    if (car.junction !== undefined) {
      const node = this.network.nodes.get(car.junction)!;
      const radius =
        Math.max(4, ...(this.network.adjacent.get(node.id) ?? []).map((e) => e.width / 2)) +
        car.length / 2 +
        1;
      if (Math.hypot(car.p.x - node.x, car.p.z - node.z) > radius && junctionId !== node.id) {
        if (this.junctions.get(node.id) === car) this.junctions.delete(node.id);
        car.junction = undefined;
      }
    }
    const road = this.network.edges.get(goal.edge ?? -1);
    if (junctionId === undefined || (road?.curved && road.bridgeId !== undefined)) return Infinity;
    const node = this.network.nodes.get(junctionId);
    if (!node || Math.abs((node.y ?? GROUND) - car.p.y) > 1.4) return Infinity;
    const half = Math.max(4, ...(this.network.adjacent.get(node.id) ?? []).map((e) => e.width / 2));
    const turnSweep = ROAD_SPECS.coach.length * (0.9 + 0.5),
      stopRadius = Math.max(half, turnSweep) + car.length / 2 + 1,
      distance = Math.hypot(car.p.x - node.x, car.p.z - node.z);
    if (distance > stopRadius + 9) return Infinity;
    let owner = this.junctions.get(node.id);
    if (
      owner &&
      (owner.junction !== node.id ||
        ['wreck', 'impounded', 'station', 'parked', 'deployed'].includes(owner.state) ||
        Math.hypot(owner.p.x - node.x, owner.p.z - node.z) > stopRadius + 9)
    ) {
      this.junctions.delete(node.id);
      if (owner.junction === node.id) owner.junction = undefined;
      owner = undefined;
    }
    if (!owner) {
      if ((this.junctionAttempt.get(node.id) ?? -1) > this.clock)
        return Math.sqrt(6 * Math.max(0, distance - stopRadius));
      this.junctionAttempt.set(node.id, this.clock + 0.1);
      const inside = this.cars
        .filter(
          (c) =>
            c !== car &&
            !['wreck', 'impounded', 'station', 'parked'].includes(c.state) &&
            Math.abs(c.p.y - car.p.y) < 2 &&
            Math.hypot(c.p.x - node.x, c.p.z - node.z) < half + 1,
        )
        .sort(
          (a, b) =>
            a.p.distanceTo(new THREE.Vector3(node.x, car.p.y, node.z)) -
            b.p.distanceTo(new THREE.Vector3(node.x, car.p.y, node.z)),
        )[0];
      // Grant to the front of an approach, not the longest follower whose
      // earlier braking horizon happened to request the junction first.
      const queue = this.cars
        .filter(
          (c) =>
            c.plan.length &&
            (c.plan[0].junction ?? c.plan[0].node) === node.id &&
            Math.hypot(c.p.x - node.x, c.p.z - node.z) <= stopRadius + 9 &&
            !['wreck', 'impounded', 'station', 'parked'].includes(c.state) &&
            Math.abs(c.p.y - car.p.y) < 2,
        )
        .sort(
          (a, b) =>
            Math.hypot(a.p.x - node.x, a.p.z - node.z) -
            a.length / 2 -
            (Math.hypot(b.p.x - node.x, b.p.z - node.z) - b.length / 2),
        );
      const fronts = queue.filter(
        (candidate) =>
          !queue.some((other) => {
            if (other === candidate || Math.cos(other.yaw - candidate.yaw) < 0.85) return false;
            const dx = other.p.x - candidate.p.x,
              dz = other.p.z - candidate.p.z,
              forwardX = -Math.sin(candidate.yaw),
              forwardZ = -Math.cos(candidate.yaw);
            return (
              dx * forwardX + dz * forwardZ > 0.1 &&
              Math.abs(dx * forwardZ - dz * forwardX) < Math.max(candidate.width, other.width)
            );
          }),
      );
      const priority = queue.find((c) => c.incident);
      let priorityFront = priority;
      if (priority) {
        const forward = new THREE.Vector3(-Math.sin(priority.yaw), 0, -Math.cos(priority.yaw));
        priorityFront =
          fronts.find((c) => {
            const delta = c.p.clone().sub(priority.p);
            return (
              delta.dot(forward) >= -0.1 &&
              Math.abs(delta.x * forward.z - delta.z * forward.x) < Math.max(c.width, priority.width) &&
              Math.cos(c.yaw - priority.yaw) > 0.85
            );
          }) ?? priority;
      }
      // Finish a vehicle already inside. Then clear the front of an emergency
      // approach, including any ordinary car immediately ahead of the siren.
      const hasExit = (candidate: TrafficVehicle) => this.junctionExitClear(candidate, node, half);
      owner =
        inside ??
        (priorityFront && hasExit(priorityFront) ? priorityFront : undefined) ??
        fronts.find(hasExit);
      if (!owner) {
        car.blockedBy = 'downstream junction space occupied';
        return Math.sqrt(6 * Math.max(0, distance - stopRadius));
      }
      if (
        owner.junction !== undefined &&
        owner.junction !== node.id &&
        this.junctions.get(owner.junction) === owner
      )
        this.junctions.delete(owner.junction);
      this.junctions.set(node.id, owner);
      owner.junction = node.id;
    }
    if (owner === car) return 6;
    // Leave the largest ordinary vehicle's turning overhang clear. A compact
    // car stopping at its own braking radius otherwise traps a coach midway
    // through the reserved turn, despite neither body having overlapped yet.
    return Math.sqrt(6 * Math.max(0, distance - stopRadius));
  }
  private junctionExitClear(
    candidate: TrafficVehicle,
    node: { id: number; x: number; z: number },
    half: number,
    plan = candidate.plan,
  ): boolean {
    const exit = junctionExitPose(
      {
        x: candidate.p.x,
        z: candidate.p.z,
        yaw: candidate.yaw,
        width: candidate.width,
        length: candidate.length,
      },
      plan,
      node,
      half + candidate.length / 2 + 1.5,
    );
    if (!exit)
      return (
        !!candidate.controlled && (plan.length > 1 || (this.network.adjacent.get(node.id)?.length ?? 0) === 1)
      );
    exit.length += 4.4;
    let free = true;
    this.actorIndex.query(exit, candidate.length + 9, (actor) => {
      if (
        !free ||
        actor === candidate ||
        actor.category !== 'vehicle' ||
        Math.abs(actor.p.y - candidate.p.y) > 2
      )
        return;
      const other = actor as TrafficVehicle;
      if (
        overlapsRoadBody(
          exit,
          { x: other.p.x, z: other.p.z, yaw: other.yaw, width: other.width, length: other.length },
          0.08,
        )
      )
        free = false;
    });
    return free;
  }
  private rerouteQueuedCar(car: TrafficVehicle): boolean {
    if (car.controlled || car.incident || car.plan.some((p) => p.reverse)) return false;
    const goal = car.plan[0],
      nodeId = goal?.junction ?? goal?.node,
      incoming = this.network.edges.get(goal?.edge ?? -1),
      node = this.network.nodes.get(nodeId ?? -1);
    if (!incoming || !node || ![incoming.from, incoming.to].includes(node.id)) return false;
    const half = Math.max(4, ...(this.network.adjacent.get(node.id) ?? []).map((e) => e.width / 2));
    // Replan at the waiting line only. A vehicle already turning must finish
    // its reserved path rather than cut across lanes or reverse into a queue.
    if (
      Math.hypot(car.p.x - node.x, car.p.z - node.z) < half + car.length / 2 + 2 ||
      this.junctionExitClear(car, node, half)
    )
      return false;
    const prior = this.network.other(incoming, node.id),
      existing = car.plan.find((p) => p.edge !== incoming.id)?.edge;
    for (const outgoing of this.network.adjacent.get(node.id) ?? []) {
      if (
        outgoing === incoming ||
        outgoing.id === existing ||
        !this.network.open(outgoing, false, car.length)
      )
        continue;
      const approach = this.network.points(incoming, prior, this.network.progress(incoming, prior, car.p)),
        plan = this.network.roundTurns(
          [...approach, ...this.network.points(outgoing, node.id)],
          car.length,
          car.p,
        );
      if (!this.junctionExitClear(car, node, half, plan)) continue;
      car.plan = plan;
      car.previousNode = prior;
      car.node = node.id;
      car.think = 0;
      car.blockedBy = '';
      this.reroutes++;
      return true;
    }
    return false;
  }
  private junctionStopRadius(node: number, length: number): number {
    return (
      Math.max(
        4,
        ROAD_SPECS.coach.length * 1.4,
        ...(this.network.adjacent.get(node) ?? []).map((e) => e.width / 2),
      ) +
      length / 2 +
      1
    );
  }
  private moveCar(car: TrafficVehicle, dt: number): void {
    if (car.kind === 'train' && car.state !== 'wreck' && car.state !== 'impounded') return;
    car.previous.copy(car.p);
    car.previousQ.copy(car.q);
    if (car.state === 'wreck' || car.state === 'impounded') {
      car.p.copy(car.body.translation());
      car.q.copy(car.body.rotation());
      return;
    }
    if (car.state === 'towed') return;
    if (car.kind === 'boat') {
      if (this.naval?.owns(car)) {
        this.naval.move(car, dt);
        return;
      }
      if (this.marine?.owns(car)) {
        this.marine.move(car, dt);
        return;
      }
      this.sail(car, dt);
      return;
    }
    car.timer -= dt;
    car.think -= dt;
    if (['parked', 'station', 'deployed', 'control'].includes(car.state)) {
      car.progressAt = this.clock;
      car.targetSpeed = car.actualSpeed = 0;
      car.blockedBy = '';
      return;
    }
    // A fully stopped car has no new swept path. Recheck on its next normal
    // traffic decision or immediately after geometry changes underneath it.
    // Timers still advance and moving/braking cars never take this shortcut.
    if (
      car.think > 0 &&
      car.actualSpeed === 0 &&
      car.targetSpeed === 0 &&
      car.restingPosition?.equals(car.p) &&
      car.restingYaw === car.yaw &&
      (car.restingStamp === this.physics.localGeometryStamp ||
        car.restingVersion ===
          this.physics.localGeometryVersion(car.p, car.length * 0.6 + Math.max(2.5, car.width)))
    ) {
      car.restingStamp = this.physics.localGeometryStamp;
      this.stoppedTrafficReuses++;
      return;
    }
    if (!car.plan.length) {
      if (car.controlled) {
        car.state = 'deployed';
        car.actualSpeed = car.targetSpeed = 0;
        return;
      }
      if (car.incident) {
        this.arrive(car);
        return;
      }
      if (car.state === 'returning') {
        car.state = 'station';
        return;
      }
      this.chooseRoad(car);
    }
    let goal = car.plan[0];
    if (!goal) {
      car.targetSpeed = 0;
      return;
    }
    if (
      !car.controlled &&
      !car.plan.some((p) => p.reverse) &&
      car.plan.at(-1)?.node !== undefined &&
      !car.plan.some((p) => p.junction !== undefined) &&
      Math.hypot(car.p.x - car.plan.at(-1)!.x, car.p.z - car.plan.at(-1)!.z) <
        this.junctionStopRadius(car.plan.at(-1)!.node!, car.length) + 9
    ) {
      const incoming = car.plan.at(-1)!,
        approach = car.plan;
      // A preceding curve records the junction it just crossed. Select this
      // next edge from the upcoming junction, not that historical turn marker.
      car.node = incoming.node!;
      this.chooseRoad(car);
      if (car.plan[0] !== incoming)
        car.plan = this.network.roundTurns([...approach, ...car.plan], car.length, car.p);
      goal = car.plan[0];
    }
    const goalDistance = Math.hypot(car.p.x - goal.x, car.p.z - goal.z);
    if (
      goalDistance <
      (goal.junction !== undefined
        ? 0.2
        : car.controlled && car.plan.length === 1
          ? 0.25
          : goal.node === undefined
            ? 0.2
            : 1.2)
    ) {
      car.plan.shift();
      if (goal.hold) {
        car.state = 'waiting';
        car.timer = goal.hold;
      }
      if (goal.node !== undefined) car.node = goal.node;
      if (!car.plan.length && car.incident) this.arrive(car);
      return;
    }
    const dir = new THREE.Vector3(goal.x - car.p.x, 0, goal.z - car.p.z).normalize();
    const urgent = this.city.regional && !!car.incident && car.id.startsWith('service:');
    if (car.think <= 0) {
      car.think = this.thinkInterval(car);
      this.navigationChecks++;
      let blocked = false,
        followingSpeed = Infinity;
      car.blockedBy = '';
      const edge = goal.edge === undefined ? undefined : this.network.edges.get(goal.edge);
      const ownedJunction = car.junction === undefined ? undefined : this.network.nodes.get(car.junction),
        clearingClosure =
          !!edge &&
          this.network.closures.has(edge.id) &&
          !!ownedJunction &&
          this.junctions.get(ownedJunction.id) === car &&
          Math.hypot(car.p.x - ownedJunction.x, car.p.z - ownedJunction.z) <
            Math.max(4, ...(this.network.adjacent.get(ownedJunction.id) ?? []).map((e) => e.width / 2)) +
              car.length / 2 +
              2;
      // A newly declared exclusion must not strand the vehicle already inside
      // the intersection. It may clear its rear onto safe intact road; hazards,
      // ground support and exact actor/structure sweeps still stop unsafe motion.
      if (
        edge &&
        !this.network.open(edge, isMilitaryVehicle(car.kind) || !!car.incident || clearingClosure, car.length)
      ) {
        blocked = true;
        car.blockedBy = `road:${edge.id}`;
      }
      const brakingLook = Math.min(
        car.length * 0.6 + Math.max(2, car.actualSpeed * 0.75),
        Math.max(car.length * 0.55, Math.hypot(goal.x - car.p.x, goal.z - car.p.z)),
      );
      const look = car.p.clone().addScaledVector(dir, brakingLook);
      const following = car.plan[1],
        bendsAhead =
          !goal.reverse &&
          !!following &&
          goalDistance < Math.max(4, car.length) &&
          dir.dot(new THREE.Vector3(following.x - goal.x, 0, following.z - goal.z).normalize()) < 0.8;
      const lookHeight = edge ? this.network.height(edge, look.x, look.z) : car.p.y;
      if (this.physics.blocked(look.x, look.z, car.width * 0.45, lookHeight + 1, car.body)) {
        blocked = true;
        car.blockedBy = 'structure';
      }
      this.actorIndex.query(car.p, car.length * 0.5 + Math.max(18, car.actualSpeed * 1.2 + 8), (other) => {
        if (
          other.id === car.id ||
          (other.category === 'vehicle' && (other as TrafficVehicle).carrier === car.id) ||
          (other.category === 'person' &&
            (other as Person).carrier !== undefined &&
            !['walk', 'boarding'].includes(other.state)) ||
          other.hidden ||
          Math.abs(other.p.y - car.p.y) > 2.5 ||
          (other.category === 'animal' && (other as Animal).kind === 'bird')
        )
          return;
        const rel = other.p.clone().sub(car.p),
          ahead = rel.dot(dir),
          side = Math.abs(rel.x * dir.z - rel.z * dir.x);
        const vehicle = other.category === 'vehicle' ? (other as TrafficVehicle) : undefined;
        const forward = vehicle
          ? new THREE.Vector3(-Math.sin(vehicle.yaw), 0, -Math.cos(vehicle.yaw))
          : undefined;
        const alignment = forward?.dot(dir) ?? 0;
        // A projected straight braking rectangle cuts across a curved turn and
        // incorrectly sees the opposite approach's stopped queue as our leader.
        // The junction owner follows its sampled curve; the exact swept pose
        // envelope below still prevents contact with every actor.
        if (
          (bendsAhead || (goal.junction !== undefined && this.junctions.get(goal.junction) === car)) &&
          (!vehicle || alignment < 0.85)
        ) {
          if (
            routeBodyOverlaps(
              { x: car.p.x, z: car.p.z, yaw: car.yaw, width: car.width, length: car.length },
              car.plan,
              {
                x: other.p.x,
                z: other.p.z,
                yaw: vehicle ? other.yaw : 0,
                width: vehicle?.width ?? 0.52,
                length: vehicle?.length ?? 0.52,
              },
              2.5 + car.actualSpeed * 0.6,
            )
          ) {
            blocked = true;
            car.blockedBy = other.id;
          }
          return;
        }
        const extent = vehicle
          ? (Math.abs(alignment) * vehicle.length) / 2 +
            (Math.sqrt(Math.max(0, 1 - alignment * alignment)) * vehicle.width) / 2
          : 0.35;
        const lateralExtent = vehicle
          ? (Math.abs(alignment) * vehicle.width) / 2 +
            (Math.sqrt(Math.max(0, 1 - alignment * alignment)) * vehicle.length) / 2
          : 0.4;
        const gap = ahead - car.length / 2 - extent;
        if (
          ahead > 0 &&
          side < car.width / 2 + lateralExtent + 0.12 &&
          gap < Math.max(14, car.actualSpeed * 1.2 + 5)
        ) {
          if (vehicle && alignment > 0.7 && !['wreck', 'parked', 'impounded'].includes(vehicle.state)) {
            followingSpeed = Math.min(followingSpeed, Math.max(0, vehicle.actualSpeed + (gap - 2.2) * 0.8));
            if (followingSpeed < 0.2) car.blockedBy = other.id;
          } else if (gap < 2.5 + car.actualSpeed * 0.6) {
            blocked = true;
            car.blockedBy = other.id;
          }
        }
      });
      if (!car.incident && this.dangerAt(car, look)) {
        blocked = true;
        car.blockedBy = 'hazard';
      }
      if (blocked) {
        car.targetSpeed = 0;
        car.blockedFor += car.think;
        this.waiting++;
        if (car.blockedFor > 4) {
          this.requestPedestrianClearance(car);
          if (car.incident && !car.controlled) this.replanResponse(car);
          else if (car.controlled) {
            this.services?.blocked(car);
            this.military?.blocked(car);
          } else if (
            !goal.reverse &&
            (car.blockedBy === 'structure' ||
              (car.blockedBy.startsWith('road:') && !this.network.closures.has(edge?.id ?? -1)) ||
              this.cars.some((c) => c.id === car.blockedBy && ['wreck', 'impounded'].includes(c.state)))
          ) {
            // Return along the current road before choosing a different junction;
            // routing from an unreached node would cut diagonally through its block.
            const currentEdge = edge;
            if (currentEdge) {
              const a = this.network.nodes.get(currentEdge.from)!,
                b = this.network.nodes.get(currentEdge.to)!;
              const forward = dir.x * (b.x - a.x) + dir.z * (b.z - a.z) >= 0;
              const laneFrom = forward ? a.id : b.id;
              car.previousNode = forward ? b.id : a.id;
              car.node = laneFrom;
              car.plan = this.network
                .points(currentEdge, laneFrom, this.network.progress(currentEdge, laneFrom, car.p), 0)
                .map((p) => ({ ...p, node: laneFrom, reverse: true, maxSpeed: 2 }));
            }
          }
          car.blockedFor = 0;
          this.reroutes++;
        }
      } else {
        car.blockedFor = 0;
        const crossingSpeed = this.crossings.speed(
          { x: car.p.x, y: car.p.y, z: car.p.z, yaw: car.yaw, width: car.width, length: car.length },
          dir,
        );
        if (crossingSpeed < 0.2) car.blockedBy = 'pedestrian crossing';
        car.targetSpeed = Math.min(
          goal.maxSpeed ?? Infinity,
          followingSpeed,
          crossingSpeed,
          edge?.curved ? Math.min(4.5, car.speed) : car.speed * (car.incident ? 1.2 : 1) * (urgent ? 5 : 1),
        );
        if (car.state === 'waiting' && car.timer <= 0) car.state = 'driving';
      }
    }
    car.targetSpeed = Math.min(car.targetSpeed, goal.reverse ? 2 : Infinity, this.junctionSpeed(car, goal));
    if (car.state === 'waiting' && car.timer > 0) car.targetSpeed = 0;
    if (urgent) {
      const nextBend = car.plan[1];
      const turn =
        nextBend &&
        dir.dot(new THREE.Vector3(nextBend.x - goal.x, 0, nextBend.z - goal.z).normalize()) < 0.93;
      const terminal = car.plan.length === 1;
      if (turn || terminal)
        car.targetSpeed = Math.min(
          car.targetSpeed,
          Math.sqrt((turn ? 4.5 ** 2 : 0) + 2 * 12 * Math.max(0, goalDistance - 0.25)),
        );
      car.actualSpeed += THREE.MathUtils.clamp(car.targetSpeed - car.actualSpeed, -18 * dt, 7 * dt);
    } else
      car.actualSpeed = THREE.MathUtils.lerp(
        car.actualSpeed,
        car.targetSpeed,
        Math.min(1, dt * (car.targetSpeed < car.actualSpeed ? 7 : 2)),
      );
    // Exponential braking otherwise keeps a stopped queue at microscopic
    // positive speeds forever. Commit a real stop below 1 cm/s, so its
    // unchanged chassis can reuse the stopped-traffic proof between decisions.
    // The next traffic decision still releases it as soon as the lane clears.
    if (car.targetSpeed < 0.01 && car.actualSpeed < 0.01) car.actualSpeed = car.targetSpeed = 0;
    const priorYaw = car.yaw,
      targetYaw =
        car.parkingYaw !== undefined && car.plan.length === 1 && goalDistance < 6
          ? car.parkingYaw
          : Math.atan2(-dir.x, -dir.z) + (goal.reverse ? Math.PI : 0);
    car.yaw += angleDelta(car.yaw, targetYaw) * Math.min(1, dt * 4);
    car.steerAngle = THREE.MathUtils.clamp(angleDelta(car.yaw, targetYaw) * 1.3, -0.45, 0.45);
    const next = car.p.clone().addScaledVector(dir, Math.min(goalDistance, car.actualSpeed * dt)),
      edge = goal.edge === undefined ? undefined : this.network.edges.get(goal.edge);
    next.y = edge ? this.network.height(edge, next.x, next.z) : goal.y;
    if (!edge || edge.curved) {
      const ground = this.physics.ground(next, 0.35, 0.5, car.body);
      if (ground) next.y = ground.y;
      // Keep the chassis above a kerb until its rear wheels have cleared it.
      // Sampling only the centre lowered the body into the road edge and could
      // deadlock the last metres of an ambulance approach without a look-ahead blocker.
      const side = new THREE.Vector3(-dir.z, 0, dir.x);
      for (const front of [-0.35, 0.35])
        for (const lateral of [-0.38, 0.38]) {
          const wheel = next
            .clone()
            .addScaledVector(dir, car.length * front)
            .addScaledVector(side, car.width * lateral);
          const support = this.physics.ground(wheel, edge?.curved ? 0.65 : 0.35, 0.55, car.body);
          if (support) next.y = Math.max(next.y, support.y);
        }
    }
    // A chassis spanning a grade joint can sit above the centre ray while its
    // axles remain supported. The reach derives from half the vehicle span and
    // this road's authored grade; a missing deck still has no upward support.
    if (
      edge?.profile &&
      !this.physics.ground(car.p, 0.35, 0.5 + car.length * (edge.maxGrade ?? 0) * 0.5, car.body)
    ) {
      this.disable(car);
      return;
    }
    const clearCentre = !this.physics.blocked(next.x, next.z, car.width * 0.42, next.y + 1, car.body);
    let clearBody = clearCentre && this.trafficSpaceClear(car, next, car.yaw, priorYaw);
    const sweepBody = (targetYaw: number): boolean => {
      const distance = car.p.distanceTo(next),
        angle = angleDelta(priorYaw, targetYaw),
        slices = Math.max(1, Math.ceil(Math.max(distance, (Math.abs(angle) * car.length) / 2) / 0.3)),
        half = car.collider.halfExtents()!,
        local = car.collider.translationWrtParent()!,
        rotation = new THREE.Quaternion(),
        previous = car.p.clone();
      // The collision chassis is slightly shorter than the visible bumper.
      // Fast travel must reserve the entire authored vehicle length.
      half.z = car.length / 2;
      for (let i = 1; i <= slices; i++) {
        const p = car.p.clone().lerp(next, i / slices),
          yaw = priorYaw + (angle * i) / slices;
        rotation.setFromAxisAngle(Y_AXIS, yaw);
        if (!(
          this.trafficSpaceClear(car, p, yaw, priorYaw) &&
          this.physics.occupiedBoxClear(
            { x: previous.x, y: previous.y + local.y, z: previous.z },
            half,
            rotation,
            p.clone().sub(previous),
            car.body,
          )
        ))
          return false;
        previous.copy(p);
      }
      return true;
    };
    if (clearBody && urgent) clearBody = sweepBody(car.yaw);
    if (
      clearCentre &&
      !clearBody &&
      car.p.distanceToSquared(next) > 1e-12 &&
      Math.abs(angleDelta(priorYaw, car.yaw)) > 1e-9 &&
      this.trafficSpaceClear(car, next, priorYaw, priorYaw) &&
      (!urgent || sweepBody(priorYaw))
    ) {
      // Clear an existing shallow rear-corner contact before steering into it.
      // The same exact envelope test must approve translation at the old yaw.
      car.yaw = priorYaw;
      car.steerAngle = 0;
      car.blockedBy = '';
      clearBody = true;
    }
    if (clearBody) car.p.copy(next);
    else {
      car.actualSpeed = car.targetSpeed = 0;
      car.yaw = priorYaw;
    }
    if (car.p.distanceToSquared(car.previous) > 1e-6) car.progressAt = this.clock;
    else if (this.clock - (car.progressAt ??= this.clock) > 5) {
      // Exact body rejection and junction/following queues also count as lack
      // of progress. Previously the clear look-ahead reset this timer before
      // a rear-corner pedestrian could ever receive a safe clearance request.
      car.progressAt = this.clock;
      this.requestPedestrianClearance(car);
      if (car.controlled) {
        this.services?.blocked(car);
        this.military?.blocked(car);
      } else this.rerouteQueuedCar(car);
    }
    if (isRoadFamily(car.kind))
      car.wheelAngle =
        ((car.wheelAngle ?? 0) -
          (car.p.distanceTo(car.previous) / ROAD_SPECS[car.kind].wheel) * (goal.reverse ? -1 : 1)) %
        (Math.PI * 2);
    car.q.setFromAxisAngle(Y_AXIS, car.yaw);
    if (edge?.profile) {
      const rise =
        this.network.height(edge, car.p.x + dir.x, car.p.z + dir.z) -
        this.network.height(edge, car.p.x - dir.x, car.p.z - dir.z);
      car.q.multiply(
        new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), Math.atan2(rise, 2)),
      );
    }
    car.body.setNextKinematicTranslation(car.p);
    car.body.setNextKinematicRotation(car.q);
    if (car.actualSpeed === 0 && car.targetSpeed === 0) {
      car.restingVersion = this.physics.localGeometryVersion(
        car.p,
        car.length * 0.6 + Math.max(2.5, car.width),
      );
      car.restingStamp = this.physics.localGeometryStamp;
      (car.restingPosition ??= new THREE.Vector3()).copy(car.p);
      car.restingYaw = car.yaw;
    } else car.restingPosition = undefined;
  }
  private sail(boat: TrafficVehicle, dt: number): void {
    const river = this.city.river?.points;
    if (!river?.length) return;
    const target = river[boat.riverNext],
      dir = new THREE.Vector3(
        target.x + (boat.id.endsWith('1') || boat.id.endsWith('3') ? 3 : -3) - boat.p.x,
        0,
        target.z - boat.p.z,
      );
    if (dir.length() < 1.5) {
      boat.riverNext += boat.riverSign;
      if (boat.riverNext >= river.length - 1 || boat.riverNext <= 0) {
        boat.riverSign *= -1;
        boat.riverNext = THREE.MathUtils.clamp(boat.riverNext, 1, river.length - 2);
      }
      return;
    }
    dir.normalize();
    const next = boat.p.clone().addScaledVector(dir, boat.speed * dt);
    if (!this.physics.blocked(next.x, next.z, 0.8, 0.8, boat.body)) boat.p.copy(next);
    boat.p.y = WATER_LEVEL - 0.565 + Math.sin(this.clock * 1.2 + boat.variant) * 0.035;
    boat.yaw += angleDelta(boat.yaw, Math.atan2(-dir.x, -dir.z)) * Math.min(1, dt * 2);
    boat.q.setFromAxisAngle(Y_AXIS, boat.yaw);
    boat.body.setNextKinematicTranslation(boat.p);
    boat.body.setNextKinematicRotation(boat.q);
  }
  plannedRoute(
    car: TrafficVehicle,
    destination: RoutePoint,
    node: number,
    emergency = true,
    from: Vec = car.p,
  ): RoutePoint[] {
    const origin = this.network.approach(from, emergency, car.length),
      finalEdge = destination.edge === undefined ? undefined : this.network.edges.get(destination.edge);
    let start = origin?.node ?? this.network.nearest(from);
    const travelling = ['driving', 'enroute', 'returning', 'waiting'].includes(car.state);
    if (origin && travelling) {
      const a = this.network.nodes.get(origin.edge.from)!,
        b = this.network.nodes.get(origin.edge.to)!;
      start = -Math.sin(car.yaw) * (b.x - a.x) - Math.cos(car.yaw) * (b.z - a.z) >= 0 ? b.id : a.id;
    }
    if (
      origin &&
      finalEdge === origin.edge &&
      (!travelling || this.network.other(origin.edge, start) === node)
    ) {
      const at = this.network.progress(origin.edge, node, from),
        end = this.network.progress(origin.edge, node, destination);
      if (end > at + 0.01) {
        const join = this.network.waypoint(origin.edge, node, at);
        return [
          ...(!travelling && Math.hypot(join.x - from.x, join.z - from.z) > 1
            ? [{ ...join, node: undefined, maxSpeed: 1.6 }]
            : []),
          ...this.network.points(origin.edge, node, at, end).map((p) => ({ ...p, node: undefined })),
        ];
      }
      if (
        emergency &&
        travelling &&
        end < at - 0.01 &&
        Math.hypot(destination.x - from.x, destination.z - from.z) < 35 &&
        (car.blockedBy === 'structure' || car.blockedBy.startsWith('road:'))
      )
        return this.network
          .points(origin.edge, node, at, end)
          .map((p) => ({ ...p, node: undefined, reverse: true }));
    }
    let path = this.network.directionalRoute(
      start,
      node,
      origin ? this.network.other(origin.edge, start) : undefined,
      finalEdge && [finalEdge.from, finalEdge.to].includes(node)
        ? this.network.other(finalEdge, node)
        : undefined,
      emergency,
      car.length,
    );
    if (origin && !travelling) {
      const alternativeStart = this.network.other(origin.edge, start),
        alternative = this.network.directionalRoute(
          alternativeStart,
          node,
          start,
          finalEdge && [finalEdge.from, finalEdge.to].includes(node)
            ? this.network.other(finalEdge, node)
            : undefined,
          emergency,
          car.length,
        );
      const cost = (nodes: number[]) => {
        if (!nodes.length) return Infinity;
        const first = this.network.nodes.get(nodes[0])!;
        let value = Math.hypot(first.x - from.x, first.z - from.z);
        for (let i = 1; i < nodes.length; i++) {
          const a = this.network.nodes.get(nodes[i - 1])!,
            b = this.network.nodes.get(nodes[i])!;
          value += Math.hypot(b.x - a.x, b.z - a.z);
        }
        return value;
      };
      if (cost(alternative) < cost(path)) {
        start = alternativeStart;
        path = alternative;
      }
    }
    const reverse: RoutePoint[] = [];
    // A newly broken dead-end road may leave no forward circuit. Back along
    // the current lane at manoeuvring speed to the last accessible junction;
    // preserve orientation and collision checks instead of turning in place.
    if (!path.length && origin && emergency) {
      const back = this.network.other(origin.edge, start);
      path = this.network.directionalRoute(
        back,
        node,
        start,
        finalEdge && [finalEdge.from, finalEdge.to].includes(node)
          ? this.network.other(finalEdge, node)
          : undefined,
        emergency,
        car.length,
      );
      if (path.length) {
        const at = this.network.progress(origin.edge, back, from);
        reverse.push(
          ...this.network
            .points(origin.edge, back, at, 0.02)
            .map((p) => ({ ...p, node: undefined, reverse: true })),
        );
        Object.assign(reverse[reverse.length - 1], { node: back, hold: 0.8 });
        start = back;
      }
    }
    if (!path.length) return [];
    const points: RoutePoint[] = [...reverse],
      first = this.network.nodes.get(start)!;
    if (origin && !reverse.length) {
      const other = this.network.other(origin.edge, start),
        a = this.network.nodes.get(other)!,
        b = this.network.nodes.get(start)!,
        advance = travelling ? Math.min(0.12, 5 / Math.hypot(b.x - a.x, b.z - a.z)) : 0,
        t = THREE.MathUtils.clamp(
          this.network.progress(origin.edge, other, origin.point) + advance,
          0.02,
          0.98,
        );
      const join = this.network.waypoint(origin.edge, other, t);
      if (Math.hypot(from.x - join.x, from.z - join.z) > 0.15) points.push({ ...join, node: undefined });
      points.push(...this.network.points(origin.edge, other, t));
    } else if (!reverse.length && Math.hypot(from.x - first.x, from.z - first.z) > 2)
      points.push({ x: first.x, y: first.y ?? GROUND, z: first.z, node: start });
    for (let i = 0; i < path.length - 1; i++) {
      const edge = this.network.edgeBetween(path[i], path[i + 1]);
      if (edge) points.push(...this.network.points(edge, path[i]));
    }
    // The final partial road must use the lane travelling away from its entry
    // junction. Reusing edge.from's lane sends an engine into opposing traffic.
    if (finalEdge && (node === finalEdge.from || node === finalEdge.to)) {
      const t = this.network.progress(finalEdge, node, destination);
      points.push(
        ...this.network
          .points(finalEdge, node, 0, THREE.MathUtils.clamp(t, 0.02, 0.98))
          .map((p) => ({ ...p, node: undefined })),
      );
    } else points.push({ ...destination, node: undefined });
    return this.network.roundTurns(points, car.length, from);
  }
  private updateResponses(dt: number): void {
    const active = new Map(this.fire.incidents().map((i) => [i.id, i]));
    for (const incident of active.values()) {
      let response = this.responses.get(incident.id);
      if (!response) {
        response = {
          id: incident.id,
          point: new THREE.Vector3(incident.point.x, incident.point.y, incident.point.z),
          status: 'pending',
          retry: 0,
          attempts: 0,
          quiet: 0,
          water: 0,
        };
        this.responses.set(incident.id, response);
      }
      response.point.set(incident.point.x, incident.point.y, incident.point.z);
      response.quiet = 0;
      response.retry -= dt;
      if (response.engine) {
        const engine = this.cars.find((c) => c.id === response!.engine);
        if (!engine || engine.state === 'wreck') {
          response.engine = undefined;
          response.status = 'pending';
          response.retry = 2;
        }
      }
      if (
        (response.status === 'pending' ||
          response.status === 'unreachable' ||
          response.status === 'complete') &&
        response.retry <= 0
      )
        this.dispatch(response, incident);
      if (response.status === 'deployed') {
        const engine = this.cars.find((c) => c.id === response!.engine);
        if (engine?.state === 'deployed' && engine.p.distanceTo(response.point) < 28) {
          const amount = dt * 0.3;
          this.fire.cool(response.id, amount);
          this.waterApplied += amount;
          response.water += amount;
        } else {
          response.status = 'unreachable';
          response.retry = 8;
        }
      }
    }
    for (const response of this.responses.values())
      if (!active.has(response.id)) {
        response.quiet += dt;
        if (response.quiet > 4 && response.status !== 'complete') {
          response.status = 'complete';
          response.retry = 12;
          this.resolved++;
          if (response.water > 0.05) this.extinguished++;
          for (const id of [response.engine, response.police]) {
            const car = this.cars.find((c) => c.id === id);
            if (car && car.state !== 'wreck') {
              car.incident = undefined;
              const node = this.network.nodes.get(car.station)!;
              car.plan = this.plannedRoute(car, { x: car.home.x, y: car.home.y, z: car.home.z }, node.id);
              car.state = car.plan.length ? 'returning' : 'station';
            }
          }
          response.engine = response.police = undefined;
        }
      }
    this.network.closures.clear();
    for (const response of this.responses.values())
      if (response.status !== 'complete' && response.police) {
        const police = this.cars.find((c) => c.id === response.police);
        if (police?.state === 'control' && response.edge !== undefined)
          this.network.closures.add(response.edge);
      }
    for (const [id, response] of this.responses)
      if (response.status === 'complete' && response.quiet > 30) this.responses.delete(id);
  }
  private dispatch(response: Response, incident: FireIncident): void {
    const reserved = this.cars.find((car) => car.id === response.engine && car.state !== 'wreck');
    if (reserved) {
      this.replanResponse(reserved);
      response.status = reserved.plan.length ? 'enroute' : 'unreachable';
      response.retry = 8;
      return;
    }
    const approach = this.network.approach(incident.point, true);
    if (
      !approach ||
      Math.hypot(approach.point.x - incident.point.x, approach.point.z - incident.point.z) > 27
    ) {
      response.status = 'unreachable';
      response.retry = 15;
      response.attempts++;
      return;
    }
    const available = this.cars
      .filter(
        (c) =>
          c.kind === 'fire' &&
          !c.incident &&
          ['station', 'returning', 'driving', 'waiting'].includes(c.state),
      )
      .sort((a, b) => a.p.distanceTo(response.point) - b.p.distanceTo(response.point));
    let engine: TrafficVehicle | undefined,
      plan: RoutePoint[] = [];
    for (const car of available) {
      const candidate = this.plannedRoute(car, approach.point, approach.node);
      if (candidate.length) {
        engine = car;
        plan = candidate;
        break;
      }
    }
    response.attempts++;
    response.retry = response.attempts < 3 ? 8 : 20;
    if (!engine) {
      response.status = 'unreachable';
      return;
    }
    engine.incident = response.id;
    engine.plan = plan;
    engine.state = 'enroute';
    engine.targetSpeed = engine.speed;
    response.engine = engine.id;
    response.destination = approach.point;
    response.edge = approach.edge.id;
    response.status = 'enroute';
    this.dispatches++;
    const police = this.cars
      .filter(
        (c) =>
          c.kind === 'police' &&
          !c.incident &&
          ['station', 'returning', 'driving', 'waiting'].includes(c.state),
      )
      .sort((a, b) => a.p.distanceTo(response.point) - b.p.distanceTo(response.point))[0];
    if (police) {
      // Hold the road before the engine's work area, with enough separation for
      // both actual vehicle colliders. A two-metre offset cannot fit a fire engine.
      const a = this.network.nodes.get(approach.edge.from)!;
      const b = this.network.nodes.get(approach.edge.to)!;
      const dx = b.x - a.x,
        dz = b.z - a.z,
        length = Math.hypot(dx, dz);
      const t = ((approach.point.x - a.x) * dx + (approach.point.z - a.z) * dz) / (length * length);
      const nearerA =
        Math.hypot(police.p.x - a.x, police.p.z - a.z) < Math.hypot(police.p.x - b.x, police.p.z - b.z);
      const stop = this.network.waypoint(
        approach.edge,
        approach.edge.from,
        THREE.MathUtils.clamp(t + (nearerA ? -12 : 12) / length, 0.08, 0.92),
      );
      const routes = [a.id, b.id]
        .map((node) => this.plannedRoute(police, stop, node))
        .filter((p) => p.length);
      const distance = (points: RoutePoint[]) =>
        points.reduce((n, p, i) => {
          const old = i ? points[i - 1] : police.p;
          return n + Math.hypot(p.x - old.x, p.z - old.z);
        }, 0);
      const route = routes.sort((a, b) => distance(a) - distance(b))[0] ?? [];
      if (route.length) {
        police.incident = response.id;
        police.plan = route;
        police.state = 'enroute';
        response.police = police.id;
      }
    }
  }
  private replanResponse(car: TrafficVehicle): void {
    const response = car.incident ? this.responses.get(car.incident) : undefined;
    if (!response) return;
    const approach = this.network.approach(response.point, true),
      route = approach ? this.plannedRoute(car, approach.point, approach.node) : [];
    if (route.length) car.plan = route;
    else {
      car.targetSpeed = 0;
      response.status = 'unreachable';
      response.retry = 8;
    }
  }
  private arrive(car: TrafficVehicle): void {
    car.actualSpeed = car.targetSpeed = 0;
    if (car.controlled) {
      car.state = 'deployed';
      return;
    }
    if (!car.incident) {
      car.state = 'station';
      return;
    }
    const response = this.responses.get(car.incident);
    if (!response) {
      car.incident = undefined;
      car.state = 'station';
      return;
    }
    car.state = car.kind === 'fire' ? 'deployed' : 'control';
    if (car.kind === 'fire') response.status = 'deployed';
  }
  private interpolation(actor: Actor, alpha: number): number {
    return (actor.stepInterval ?? 0) > 0.02
      ? Math.min(1, ((actor.stepDebt ?? 0) + alpha / 60) / actor.stepInterval!)
      : alpha;
  }
  militaryTurretPose(car: TrafficVehicle, alpha = 1): THREE.Matrix4 {
    const height = isMilitaryVehicle(car.kind) ? MILITARY_SPECS[car.kind].gun : 0;
    const p = car.previous.clone().lerp(car.p, alpha),
      q = car.previousQ.clone().slerp(car.q, alpha);
    p.add(new THREE.Vector3(0, height, 0).applyQuaternion(q));
    q.multiply(new THREE.Quaternion().setFromAxisAngle(Y_AXIS, car.turretYaw ?? 0));
    q.multiply(new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), car.turretPitch ?? 0));
    return new THREE.Matrix4().compose(p, q, new THREE.Vector3(1, 1, 1));
  }
  private prepareCharacter(actor: Person, alpha: number, visible: boolean): void {
    this.dummy.position.copy(actor.previous).lerp(actor.p, alpha);
    const fallen =
      actor.poseTilt === undefined && ['incapacitated', 'carried', 'recovering'].includes(actor.state);
    if (fallen) this.dummy.position.y += 0.28;
    const p = this.dummy.position,
      prior = actor.renderRoot;
    if (
      prior &&
      prior.yaw === actor.yaw &&
      prior.scale === actor.scale &&
      prior.tilt === actor.poseTilt &&
      prior.fallen === fallen
    ) {
      // A straight walk changes translation, not the body's linear basis.
      prior.matrix.setPosition(p);
      prior.x = p.x;
      prior.y = p.y;
      prior.z = p.z;
      this.dummy.matrix.copy(prior.matrix);
    } else {
      this.dummy.rotation.set(0, actor.yaw, fallen ? Math.PI / 2 : 0);
      this.dummy.scale.setScalar(actor.scale);
      if (actor.poseTilt !== undefined) this.dummy.rotateX(actor.poseTilt);
      this.dummy.updateMatrix();
      const cached: NonNullable<Person['renderRoot']> = prior ?? {
        x: 0,
        y: 0,
        z: 0,
        yaw: 0,
        scale: 1,
        fallen: false,
        matrix: new THREE.Matrix4(),
      };
      cached.x = p.x;
      cached.y = p.y;
      cached.z = p.z;
      cached.yaw = actor.yaw;
      cached.scale = actor.scale;
      cached.tilt = actor.poseTilt;
      cached.fallen = fallen;
      cached.matrix.copy(this.dummy.matrix);
      actor.renderRoot = cached;
    }
    let coarse = false;
    if (
      visible &&
      this.viewCamera &&
      !actor.carry &&
      actor.seatHeight === undefined &&
      actor.poseTilt === undefined &&
      !fallen &&
      !actor.carrier &&
      !['parachuting', 'surrendering', 'stretching', 'sitting', 'resting'].includes(actor.state)
    ) {
      const m = this.viewCamera.matrixWorldInverse.elements;
      const depth = -(m[2] * p.x + m[6] * p.y + m[10] * p.z + m[14]);
      const pixels =
        (1.82 * actor.scale * this.presentationHeight * this.viewCamera.projectionMatrix.elements[5]) /
        (2 * Math.max(0.1, depth));
      // Promote before small features are readable; hysteresis avoids churn.
      coarse = actor.presentationCoarse =
        pixels < (actor.presentationCoarse ? (IS_LITE ? 42 : 24) : IS_LITE ? 34 : 20);
    } else if (visible) actor.presentationCoarse = false;
    this.characters.render(
      actor.id,
      this.dummy.matrix,
      alpha,
      this.clock,
      actor.poseTilt ? 'lying' : actor.state,
      !!actor.carry,
      actor.color,
      visible,
      actor.seatHeight === undefined ? undefined : actor.seatHeight / actor.scale,
      actor.seatBlend,
      coarse,
    );
    actor.targetPoseStamp = this.poseStamp;
  }
  /** Combat and attachments evaluate the original current articulated model. */
  personPose(person: Person, anchor: string): THREE.Matrix4 | undefined {
    this.refreshEntityTargets();
    this.prepareCharacter(person, 1, false);
    return this.characters.pose(person.id, anchor);
  }
  actorsNear(point: Vec, radius: number, visit: (actor: Person | TrafficVehicle | Animal) => void): void {
    if (this.indexAge >= 0.25) this.refreshSpatial();
    this.actorIndex.query(point, radius, (a) => visit(a as Person | TrafficVehicle | Animal));
  }
  render(alpha = 1): void {
    if (!this.visible) return;
    this.poseStamp++;
    this.renderAlpha = alpha;
    if (this.viewCamera) {
      this.viewProjection.multiplyMatrices(
        this.viewCamera.projectionMatrix,
        this.viewCamera.matrixWorldInverse,
      );
      this.viewFrustum.setFromProjectionMatrix(this.viewProjection);
    }
    this.submittedResidents = this.submittedCrew = 0;
    this.occludedResidents = 0;
    if (this.viewLight)
      this.lightDirection.subVectors(this.viewLight.position, this.viewLight.target.position);
    this.services?.render();
    this.airSupport?.render();
    this.aviation?.ground.render();
    this.military?.security.render(alpha);
    this.military?.air.render(alpha);
    this.airstrikes?.render(alpha);
    this.invasion?.render(alpha);
    this.refreshEntityTargets();
    this.characters.begin();
    for (const mesh of this.meshes.values()) mesh.count = 0;
    for (const actor of [...this.people, ...this.animals]) {
      const kind = (actor as Animal).kind;
      const actorAlpha = this.interpolation(actor, alpha);
      this.dummy.position.copy(actor.previous).lerp(actor.p, actorAlpha);
      if (actor.category === 'person') {
        if (!this.actorVisible(actor) || !this.inView(this.dummy.position, 8 * actor.scale)) continue;
        if (
          this.viewCamera &&
          this.viewLight &&
          !actor.carry &&
          actor.poseTilt === undefined &&
          !['incapacitated', 'carried', 'recovering', 'parachuting'].includes(actor.state) &&
          this.interiorOcclusion.hidden(
            actor.layer,
            this.dummy.position,
            actor.scale,
            this.viewCamera.position,
            this.lightDirection,
          )
        ) {
          this.occludedResidents++;
          continue;
        }
        this.prepareCharacter(actor, actorAlpha, true);
      } else {
        this.dummy.rotation.set(0, actor.yaw, 0);
        this.dummy.scale.setScalar(actor.scale);
        if (actor.state === 'incapacitated' || actor.state === 'carried' || actor.state === 'recovering') {
          this.dummy.rotation.z = Math.PI / 2;
          this.dummy.position.y += 0.28;
        } else if ((actor as Animal).kind === 'bird') {
          if (actor.state !== 'perch') this.dummy.rotation.x = -0.06;
        } else if (actor.category === 'animal' && ['walk', 'flee', 'roam'].includes(actor.state))
          this.dummy.position.y += Math.abs(Math.sin(this.clock * actor.speed * 7 + actor.variant)) * 0.04;
        else if (actor.category === 'animal' && actor.state === 'rest') {
          this.dummy.scale.y *= 0.76;
          this.dummy.position.y -= 0.015;
        } else if (actor.category === 'animal' && actor.state === 'drink') this.dummy.rotation.x = -0.2;
        else if (actor.state === 'graze')
          this.dummy.rotation.x = 0.13 * Math.sin(this.clock * 0.9 + actor.variant);
        this.dummy.updateMatrix();
      }
      const visible = this.actorVisible(actor) && this.inView(this.dummy.position, 8 * actor.scale);
      if (actor.category === 'animal' && visible)
        this.instance(
          (actor as Animal).birdFamily ? `bird:${(actor as Animal).birdFamily}` : kind,
          actor.color,
        );
      if (visible && actor.category === 'person') {
        if (actor.id.startsWith('crew:')) this.submittedCrew++;
        else this.submittedResidents++;
      }
      this.targetMatrices.get(actor.id)?.copy(this.dummy.matrix);
      this.entities?.sync(actor.id);
      if (visible && actor.category === 'animal' && (actor as Animal).birdFamily) {
        const original = this.dummy.quaternion.clone();
        for (const side of [-1, 1]) {
          this.dummy.quaternion.copy(original);
          if (['perch', 'incapacitated', 'falling'].includes(actor.state)) this.dummy.rotateY(-side * 1.08);
          else
            this.dummy.rotateZ(
              side *
                Math.sin(this.clock * ((actor as Animal).birdFamily === 'gull' ? 5 : 11) + actor.variant) *
                0.55,
            );
          this.instance(`bird-wing:${(actor as Animal).birdFamily}:${side}`, actor.color);
        }
        this.dummy.quaternion.copy(original);
      }
      if (actor.carry && visible) {
        const hand = actor.category === 'person' ? this.characters.pose(actor.id, 'hand:right') : undefined;
        if (hand) {
          hand.decompose(this.dummy.position, this.dummy.quaternion, this.dummy.scale);
          this.dummy.position.add(new THREE.Vector3(0, -0.1, 0).applyQuaternion(this.dummy.quaternion));
        } else
          this.dummy.position.add(new THREE.Vector3(0.32, 0.76, -0.2).applyQuaternion(this.dummy.quaternion));
        this.dummy.scale.setScalar(1);
        this.instance('carry', 0xffffff);
      }
    }
    this.characters.finish();
    for (const car of this.cars) {
      const carAlpha = this.interpolation(car, alpha);
      this.dummy.position.copy(car.previous).lerp(car.p, carAlpha);
      this.dummy.quaternion.copy(car.previousQ).slerp(car.q, carAlpha);
      this.dummy.scale.setScalar(1);
      this.dummy.updateMatrix();
      this.targetMatrices.get(car.id)?.copy(this.dummy.matrix);
      this.entities?.sync(car.id);
      if (!this.inView(this.dummy.position, car.length * 0.7 + 9)) continue;
      this.instance(
        car.kind === 'boat' && car.boatFamily ? `boat:${car.boatFamily}` : car.kind,
        car.state === 'wreck' ? 0x827f77 : car.color,
      );
      if (isMilitaryVehicle(car.kind) && MILITARY_SPECS[car.kind].gun) {
        const turret = this.militaryTurretPose(car, alpha);
        turret.decompose(this.dummy.position, this.dummy.quaternion, this.dummy.scale);
        this.instance(`turret:${car.kind}`, car.state === 'wreck' ? 0x827f77 : car.color);
        this.dummy.position.copy(car.previous).lerp(car.p, alpha);
        this.dummy.quaternion.copy(car.previousQ).slerp(car.q, alpha);
      }
      if (car.kind === 'boat' && car.cargo) {
        const original = this.dummy.position.clone(),
          q = this.dummy.quaternion.clone();
        this.dummy.position.add(new THREE.Vector3(0.45, 0.95, car.length * 0.27).applyQuaternion(q));
        this.instance('carry', 0xc7ac83);
        this.dummy.position.copy(original);
        this.dummy.quaternion.copy(q);
      }
      if (car.kind === 'troop-truck' && car.cargo) {
        const origin = this.dummy.position.clone(),
          q = this.dummy.quaternion.clone();
        for (let i = 0; i < Math.min(8, car.cargo); i++) {
          this.dummy.position
            .copy(origin)
            .add(
              new THREE.Vector3(
                i % 2 ? 0.24 : -0.24,
                1.25 + Math.floor(i / 4) * 0.44,
                0.2 + Math.floor((i % 4) / 2) * 0.55,
              ).applyQuaternion(q),
            );
          this.instance('carry', 0xbbaa76);
        }
        this.dummy.position.copy(origin);
        this.dummy.quaternion.copy(q);
      }
      if (isRoadFamily(car.kind)) {
        const spec = ROAD_SPECS[car.kind],
          q = this.dummy.quaternion.clone(),
          centre = this.dummy.position.clone();
        const bike = car.kind === 'motorcycle' || car.kind === 'scooter';
        const axles = spec.axles === 3 ? [-0.33, 0.19, 0.35] : [-0.33, 0.33];
        for (const z of axles)
          for (const x of bike ? [0] : [-spec.width * 0.48, spec.width * 0.48]) {
            this.dummy.position
              .copy(centre)
              .add(new THREE.Vector3(x, spec.wheel, spec.length * z).applyQuaternion(q));
            this.dummy.quaternion
              .copy(q)
              .multiply(new THREE.Quaternion().setFromAxisAngle(Y_AXIS, z < 0 ? (car.steerAngle ?? 0) : 0))
              .multiply(
                new THREE.Quaternion().setFromAxisAngle(new THREE.Vector3(1, 0, 0), car.wheelAngle ?? 0),
              );
            this.dummy.scale.set(bike ? 0.65 : 1, spec.wheel / 0.34, spec.wheel / 0.34);
            this.instance('road-wheel', 0xffffff);
          }
      }
      if (car.kind === 'delivery') {
        this.dummy.quaternion.copy(car.previousQ).slerp(car.q, alpha);
        this.dummy.position
          .copy(car.previous)
          .lerp(car.p, alpha)
          .add(new THREE.Vector3(0, 0, (car.doorOpen ?? 0) * 1.5).applyQuaternion(this.dummy.quaternion));
        this.dummy.scale.setScalar(1);
        this.instance('delivery-door', car.color);
      }
      if (car.kind === 'train')
        for (const sign of [-1, 1]) {
          this.dummy.position
            .copy(car.previous)
            .lerp(car.p, alpha)
            .add(
              new THREE.Vector3(0, 0, sign * (car.doorOpen ?? 0) * 0.72).applyQuaternion(
                this.dummy.quaternion,
              ),
            );
          this.instance(`train-door:${sign}`, car.color);
        }
      if (car.kind === 'bus' || car.kind === 'coach')
        for (const sign of [-1, 1]) {
          this.dummy.quaternion.copy(car.previousQ).slerp(car.q, alpha);
          this.dummy.position
            .copy(car.previous)
            .lerp(car.p, alpha)
            .add(
              new THREE.Vector3(0, 0, sign * (car.doorOpen ?? 0) * 0.8).applyQuaternion(
                this.dummy.quaternion,
              ),
            );
          this.dummy.scale.setScalar(1);
          this.instance(`${car.kind}-door:${sign}`, car.color);
        }
    }
    let hoseCount = 0;
    for (const response of this.responses.values()) {
      const engine = this.cars.find((c) => c.id === response.engine);
      if (response.status === 'deployed' && engine?.state === 'deployed')
        for (let i = 0; i < 2; i++) {
          const p = engine.p
            .clone()
            .add(
              new THREE.Vector3(
                Math.cos(engine.yaw) * (2 + i * 0.65),
                0,
                -Math.sin(engine.yaw) * (2 + i * 0.65),
              ),
            );
          if (this.physics.blocked(p.x, p.z, 0.3, p.y + 0.8))
            p.copy(engine.p).add(new THREE.Vector3(0, 0, engine.length * 0.6));
          this.dummy.position.copy(p);
          this.dummy.rotation.set(0, Math.atan2(p.x - response.point.x, p.z - response.point.z), 0);
          this.dummy.scale.setScalar(1);
          this.instance('crew', 0xd7b27d);
          if (hoseCount < 4) {
            this.hosePositions.set(
              [p.x, p.y + 1, p.z, response.point.x, response.point.y + 0.7, response.point.z],
              hoseCount++ * 6,
            );
          }
        }
      const police = this.cars.find((c) => c.id === response.police);
      if (response.status !== 'complete' && police?.state === 'control')
        for (let i = 0; i < 4; i++) {
          this.dummy.position.set(police.p.x + (i - 1.5) * 1.1, police.p.y, police.p.z + 2.5);
          this.dummy.rotation.set(0, 0, 0);
          this.dummy.scale.setScalar(1);
          this.instance('cones', 0xffffff);
        }
    }
    this.hoses.geometry.setDrawRange(0, hoseCount * 2);
    this.hoses.geometry.attributes.position.needsUpdate = true;
    this.wakePool.forEach((wake, i) => {
      const age = 4 - wake.life;
      this.dummy.position.set(wake.p.x, 0.074, wake.p.z);
      this.dummy.rotation.set(0, wake.yaw, 0);
      this.dummy.scale.set(1.3 + age * 0.8, 1, 1 + age * 0.6);
      this.dummy.updateMatrix();
      this.wakes.setMatrixAt(i, this.dummy.matrix);
    });
    this.wakes.count = this.wakePool.length;
    if (this.wakes.count) {
      this.wakes.instanceMatrix.clearUpdateRanges();
      this.wakes.instanceMatrix.addUpdateRange(0, this.wakes.count * 16);
      this.wakes.instanceMatrix.needsUpdate = true;
    }
    for (const mesh of this.meshes.values()) {
      if (!mesh.count) continue;
      // Shared buffers retain their capacity for a full city, but only the
      // submitted prefix changes. Uploading every unused vehicle/wildlife slot
      // consumed render time even with an unchanged population and view.
      mesh.instanceMatrix.clearUpdateRanges();
      mesh.instanceMatrix.addUpdateRange(0, mesh.count * 16);
      mesh.instanceMatrix.needsUpdate = true;
      if (mesh.instanceColor) {
        mesh.instanceColor.clearUpdateRanges();
        mesh.instanceColor.addUpdateRange(0, mesh.count * 3);
        mesh.instanceColor.needsUpdate = true;
      }
    }
  }
  /** Generous shadow and motion margins keep entering actors ready before they
   * are visible. Their logical state and attachment root are always updated. */
  private inView(point: THREE.Vector3, radius: number): boolean {
    if (!this.viewCamera) return true;
    this.viewSphere.center.copy(point);
    this.viewSphere.radius = radius;
    return this.viewFrustum.intersectsSphere(this.viewSphere);
  }
  private instance(kind: string, color: number): void {
    const mesh = this.meshes.get(kind)!;
    if (mesh.count >= mesh.instanceMatrix.count) return;
    this.dummy.updateMatrix();
    mesh.setMatrixAt(mesh.count, this.dummy.matrix);
    this.color.setHex(color);
    mesh.setColorAt(mesh.count++, this.color);
  }
  show(visible: boolean): void {
    this.visible = visible;
    for (const mesh of this.meshes.values()) mesh.visible = visible;
    for (const mesh of this.characters.meshes.values()) mesh.visible = visible;
    this.wakes.visible = this.hoses.visible = visible;
  }
  snapshot(): Record<string, number | string> {
    return {
      ...this.coastStaff?.snapshot(),
      ...this.powerPlant?.snapshot(),
      ...this.military?.snapshot(),
      ...this.invasion?.snapshot(),
      ...this.airstrikes?.snapshot(),
      ...this.drivers?.snapshot(),
      ...this.shelters?.snapshot(),
      pedestrians: this.people.length,
      logicalResidents: this.people.filter((p) => !p.id.startsWith('crew:')).length,
      detailedResidents: this.people.filter((p) => p.id.startsWith('person-') && !p.ambientCoarse).length,
      coarseResidents: this.people.filter((p) => p.ambientCoarse).length,
      unloadedResidents: this.people.filter((p) => p.streamedOut).length,
      renderedResidents: this.visible ? this.submittedResidents : 0,
      occludedResidents: this.visible ? this.occludedResidents : 0,
      renderedCrew: this.visible ? this.submittedCrew : 0,
      posedCharacters: this.characters.posedThisFrame,
      translatedCharacters: this.characters.translatedThisFrame,
      coarseCharacters: this.characters.coarseThisFrame,
      characterMatrixWrites: this.characters.matrixWritesThisFrame,
      nearResidents: this.people.filter(
        (p) => Math.hypot(p.p.x - this.viewFocus.x, p.p.z - this.viewFocus.z) <= 180,
      ).length,
      scheduledResidents: this.people.filter((p) => (p.stepInterval ?? 0) > 0.02).length,
      ordinaryVehicles: this.cars.filter((c) => !c.controlled && c.kind !== 'boat').length,
      traffic: this.cars.filter((c) => c.kind !== 'boat').length,
      boats: this.cars.filter((c) => c.kind === 'boat').length,
      animals: this.animals.filter((a) => a.kind !== 'bird').length,
      birds: this.animals.filter((a) => a.kind === 'bird').length,
      npcFleeing: this.people.filter((p) => p.state === 'flee').length,
      npcIncapacitated: this.people.filter((p) => p.state === 'incapacitated').length,
      npcReactions: this.reactions,
      animalReactions: this.animalReactions,
      animalFleeing: this.animals.filter((a) => a.state === 'flee').length,
      animalDrinks: this.animals.reduce((n, a) => n + (a.drinks ?? 0), 0),
      animalRests: this.animals.reduce((n, a) => n + (a.rests ?? 0), 0),
      wrecks: this.cars.filter((c) => c.state === 'wreck').length,
      trafficWaiting: this.cars.filter(
        (c) =>
          c.targetSpeed === 0 && !['wreck', 'station', 'parked', 'deployed', 'control'].includes(c.state),
      ).length,
      directEntityImpacts: this.directImpacts,
      reroutes: this.reroutes,
      navigationChecks: this.navigationChecks,
      stoppedTrafficReuses: this.stoppedTrafficReuses,
      footRouteSteps: this.footRouteSteps,
      footFreeSteps: this.footFreeSteps,
      footSteeringChecks: this.footSteeringChecks,
      footSteeringWaits: this.footSteeringWaits,
      footFlatSteps: this.footFlatSteps,
      responders: this.cars.filter((c) => c.kind === 'fire' || c.kind === 'police').length,
      dispatches: this.dispatches,
      activeIncidents: [...this.responses.values()].filter((r) => r.status !== 'complete').length,
      unreachableIncidents: [...this.responses.values()].filter((r) => r.status === 'unreachable').length,
      crewsDeployed: this.cars.filter((c) => c.kind === 'fire' && c.state === 'deployed').length * 2,
      roadClosures: this.network.closures.size,
      extinguishedIncidents: this.extinguished,
      resolvedIncidents: this.resolved,
      waterApplied: this.waterApplied,
      actors: this.people.length + this.cars.length + this.animals.length,
      visiblePopulation: this.visible ? 1 : 0,
      characterSignatures: new Set([...this.characters.entries.values()].map((e) => e.style.signature)).size,
      characterPartInstances: [...this.characters.meshes.values()].reduce((n, m) => n + m.count, 0),
      ...this.civil?.snapshot(),
      ...this.services?.snapshot(),
      ...this.rail?.snapshot(),
      ...this.aviation?.snapshot(),
      ...this.marine?.snapshot(),
      ...this.airSupport?.snapshot(),
      ...this.buses?.snapshot(),
      ...this.logistics?.snapshot(),
      ...this.shopStaff?.snapshot(),
    };
  }
  actors(): Record<string, number | string>[] {
    return [...this.people, ...this.cars, ...this.animals].map((a) => ({
      id: a.id,
      name: a.name,
      category: a.category,
      kind:
        a.category === 'vehicle'
          ? (a as TrafficVehicle).kind
          : a.category === 'animal'
            ? (a as Animal).kind
            : 'pedestrian',
      x: a.p.x,
      y: a.p.y,
      z: a.p.z,
      yaw: a.yaw,
      state: a.state,
      layer: a.layer ?? 'street',
      support: a.support ?? -1,
      ...(a.category === 'animal'
        ? {
            habitat: (a as Animal).pasture ?? -1,
            birdFamily: (a as Animal).birdFamily ?? '',
            drinks: (a as Animal).drinks ?? 0,
            rests: (a as Animal).rests ?? 0,
            perch: (a as Animal).perch ?? -1,
          }
        : {}),
      ...(a.category === 'person'
        ? {
            appearance: this.characters.ensure(a.id).style.signature,
            gaitPhase: this.characters.ensure(a.id).motion.phase,
          }
        : {}),
      ...(a.category === 'vehicle'
        ? {
            body: (a as TrafficVehicle).body.handle,
            health: (a as TrafficVehicle).health,
            width: (a as TrafficVehicle).width,
            length: (a as TrafficVehicle).length,
            heat: (a as TrafficVehicle).heat,
            incident: (a as TrafficVehicle).incident ?? '',
            dynamic: (a as TrafficVehicle).body.isDynamic() ? 1 : 0,
            blockedBy: (a as TrafficVehicle).blockedBy,
            yaw: a.yaw,
            actualSpeed: (a as TrafficVehicle).actualSpeed,
            junction: (a as TrafficVehicle).junction ?? -1,
            nextJunction:
              (a as TrafficVehicle).plan[0]?.junction ?? (a as TrafficVehicle).plan[0]?.node ?? -1,
            junctionOwner:
              this.junctions.get(
                (a as TrafficVehicle).plan[0]?.junction ?? (a as TrafficVehicle).plan[0]?.node ?? -1,
              )?.id ?? '',
            routePoints: (a as TrafficVehicle).plan.length,
            routeGoalX: (a as TrafficVehicle).plan[0]?.x ?? a.p.x,
            routeGoalZ: (a as TrafficVehicle).plan[0]?.z ?? a.p.z,
          }
        : {}),
    }));
  }
  clear(): void {
    for (const step of this.clearSteps()) void step;
  }
  *clearSteps(): Generator<void> {
    this.invasion?.reset();
    this.characters.clear();
    this.junctions.clear();
    this.junctionAttempt.clear();
    this.crossings.clear();
    this.logistics?.clear();
    yield;
    this.shopStaff?.clear();
    this.coastStaff?.clear();
    this.powerPlant?.clear();
    this.military?.clear();
    this.shelters?.clear();
    this.drivers?.clear();
    this.civil?.clear();
    yield;
    this.services?.clear();
    yield;
    this.rail?.clear();
    yield;
    this.airstrikes?.clear();
    this.aviation?.clear();
    yield;
    this.marine?.clear();
    this.naval?.clear();
    this.buses?.clear();
    this.airSupport?.clear();
    yield;
    for (const id of this.targetMatrices.keys()) this.entities?.unregister(id);
    this.targetMatrices.clear();
    this.targetsDirty = true;
    for (const car of this.cars) {
      this.fire.unregister(`vehicle:${car.id}`);
      if (car.kind === 'boat') this.water?.unregister(`boat:${car.id}`);
      if (
        this.bodyWorld === this.physics.world &&
        this.physics.world.getRigidBody(car.body.handle) === car.body
      )
        this.physics.world.removeRigidBody(car.body);
      yield;
    }
    this.people = [];
    this.cars = [];
    this.animals = [];
    this.hazards = [];
    this.fireHazards = [];
    this.responses.clear();
    this.seen.clear();
    this.byBody.clear();
    this.actorIndex.clear();
    this.footIndex.clear();
    this.dangerIndex.clear();
    this.wakePool = [];
    this.network.clear();
    for (const mesh of this.meshes.values()) mesh.count = 0;
    this.wakes.count = 0;
    this.hoses.geometry.setDrawRange(0, 0);
    this.decisionDebt = 0;
    this.clock =
      this.indexAge =
      this.responseAge =
      this.wakeAge =
      this.reactions =
      this.waiting =
      this.directImpacts =
      this.reroutes =
      this.dispatches =
      this.extinguished =
      this.resolved =
      this.waterApplied =
      this.animalReactions =
      this.navigationChecks =
      this.stoppedTrafficReuses =
      this.footRouteSteps =
      this.footFreeSteps =
      this.footSteeringChecks =
      this.footSteeringWaits =
      this.footFlatSteps =
        0;
  }
  dispose(): void {
    this.services?.dispose();
    this.airSupport?.dispose();
    this.airstrikes?.dispose();
    this.aviation?.dispose();
    this.military?.dispose();
    this.invasion?.dispose();
    this.clear();
    this.characters.dispose();
    for (const [key, mesh] of this.meshes) {
      mesh.removeFromParent();
      mesh.geometry.dispose();
      if (key === 'cones') (mesh.material as THREE.Material).dispose();
    }
    this.meshes.clear();
    this.wakes.removeFromParent();
    this.wakes.geometry.dispose();
    (this.wakes.material as THREE.Material).dispose();
    this.hoses.removeFromParent();
    this.hoses.geometry.dispose();
    this.hoses.material.dispose();
  }
}
